import React, { useState, useMemo, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  deleteServiceInitiativeRow,
  createServiceStatusDescription,
  createServiceTask,
  fetchAllServiceTasks,
  fetchServiceInitiativeAreas,
  fetchDirectoryUsers,
  fetchLatestServiceStatusDescriptions,
  fetchServiceStatusDescriptions,
  fetchServiceTasks,
  ServiceInitiativeArea,
  ServiceStatusDescription,
  ServiceTask,
  DirectoryUser,
  setServiceInitiativeFavorite,
  updateServiceTask,
  upsertServiceInitiativeRow,
  useCSVData,
  normalizeFuncionalidadValue,
  CSVRow,
} from '../hooks/useCSVData';
import { getDirectoryJsonUrl, getBlobUrlByConnectionString } from '../auth/blobConfig';
import Papa from 'papaparse';
import LoadingSpinner from '../components/SMLoadingSpinner';
import IntakeChatPanel from '../components/IntakeChatPanel';
import { Search, RefreshCw, AlertCircle, Download, X, Filter, ChevronDown, Activity, Save, PlusCircle, Trash2, LayoutGrid, Rows3, Users, Calendar, ArrowUpDown, Sparkles, ChevronRight, Star, User, Building2, FileDown, List, Clock, CheckCircle2, Plus, MessageSquarePlus } from 'lucide-react';
import { exportServiceInitiativesPdfReport } from '../services/initiativePdfReport';
import { deleteAzureOpenAIFile, deleteVectorStore } from '../services/azureOpenAI';

function getFieldValue(row: CSVRow, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function getServiceName(row: CSVRow): string {
  return getFieldValue(row, ['NombreProceso', 'Nombre del servicio']);
}

function getServiceEstado(row: CSVRow): string {
  return getFieldValue(row, ['Estado_implementacion', 'Estado implementación']);
}

function getModifiedDateValue(row: CSVRow): string {
  return getFieldValue(row, ['Modificado']);
}

function parseModifiedDate(raw: string | undefined): Date | null {
  if (!raw || !raw.trim()) return null;
  const value = raw.trim();

  const ddmmyyyy = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (ddmmyyyy) {
    const [, d, m, y, hh = '0', mm = '0', ss = '0'] = ddmmyyyy;
    const dt = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}


function isGptCeuFunctionality(row: CSVRow): boolean {
  const funcionalidad = normalizeFuncionalidadValue(row['Funcionalidad']).toLowerCase();
  return funcionalidad === 'gpt-ceu';
}

function isFavorite(row: CSVRow): boolean {
  return String(row.__is_favorite || '').toLowerCase() === 'true';
}

function isDefaultVisible(row: CSVRow): boolean {
  const estado = normalizeSearchText(getServiceEstado(row));
  if (estado === 'cancelado') return false;
  if (isFavorite(row)) return true;
  return estado === 'implementado' || estado.includes('desarrollo');
}

function parseUsersList(raw: string | undefined): string[] {
  const parsed = parseUsers(raw);
  if (!parsed || parsed === '-') return [];
  return parsed
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

interface UserEntry {
  name: string;
  email: string;
}

function parseUserEntries(raw: string | undefined): UserEntry[] {
  if (!raw || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item: any) => {
          const email = String(item?.user_mail || item?.correo || item?.mail || item?.email || '').trim();
          const name = String(item?.nombre || item?.name || item?.displayName || item?.display_name || '').trim();
          return { name: name || email, email };
        })
        .filter((item) => item.name || item.email);
    }
  } catch {
    // fallback below
  }

  return raw
    .split(/[;,]/)
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => {
      const email = extractEmailsFromText(value)[0] || '';
      return { name: value.replace(email, '').trim() || email || value, email };
    });
}

function parseRoleLabels(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // fallback below
  }

  return raw
    .replace(/^\[|\]$/g, '')
    .replace(/"/g, '')
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

interface DirectoryEntry {
  email: string;
  name: string;
}

function getDirectoryUserFullName(user: DirectoryUser): string {
  const explicit = String(user.nombre_completo || '').trim();
  const composed = [user.nombre, user.apellidos]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');

  if (user.apellidos && String(user.apellidos).trim()) return composed || explicit;
  return explicit || composed;
}

function mapDirectoryUsersToDirectoryEntries(users: DirectoryUser[]): DirectoryEntry[] {
  return users
    .map((user) => ({
      email: normalizeEmail(String(user.mail || '')),
      name: getDirectoryUserFullName(user),
    }))
    .filter((entry) => entry.email);
}

function buildNameByEmail(directoryEntries: DirectoryEntry[], databaseEntries: DirectoryEntry[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const entry of directoryEntries) map[entry.email] = entry.name;
  for (const entry of databaseEntries) {
    if (entry.name) map[entry.email] = entry.name;
  }
  return map;
}

const FUNCIONALIDAD_OPTIONS = ['GPT-CEU', 'Az-Function', 'Agente Copilot', 'Avatar'];
const ESTADO_OPTIONS = ['Implementado', 'Cancelado', 'En desarrollo'];
const ROLE_OPTIONS = ['TODOS', 'ALUMNO', 'PAS', 'PDI', 'ADMIN'];
const BOOL_OPTIONS = ['False', 'True'];
const CENTRO_OPTIONS = ['UCH', 'USP', 'UAO', 'UF3', 'General'];
const SERVICIO_OPTIONS = ['Azure', 'OpenAI'];
const CREATE_SERVICE_CSV_FIELDS = [
  'NombreProceso',
  'descripcion',
  'ID',
  'Centro',
  'Role',
  'Whatsapp',
  'Usuario',
  'Fecha',
  'Sinidentificacion',
  'Fecha novedad proceso',
  'Estado implementación',
];
const ACCESS_CSV_BLOB_NAME = 'lista_de_acceso/acceso_des.csv';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace('_', ' ')
    .trim();
}

function scoreDirectoryEntry(entry: DirectoryEntry, query: string): number {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return -1;

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (!tokens.length) return -1;

  const email = normalizeSearchText(entry.email);
  const name = normalizeSearchText(entry.name || '');
  const words = `${name} ${email}`.split(/[\s@._-]+/).filter(Boolean);

  let score = 0;
  for (const token of tokens) {
    let matched = false;

    if (email === token || name === token) {
      score += 120;
      matched = true;
    } else if (email.startsWith(token)) {
      score += 90;
      matched = true;
    } else if (words.some((w) => w.startsWith(token))) {
      score += 75;
      matched = true;
    } else if (email.includes(token)) {
      score += 55;
      matched = true;
    } else if (name.includes(token)) {
      score += 45;
      matched = true;
    }

    if (!matched) return -1;
  }

  if (name && name.includes(normalizedQuery)) score += 20;
  if (email.includes(normalizedQuery)) score += 25;

  return score;
}

function extractEmailsFromText(raw: string): string[] {
  const matches = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
  const uniq = new Set(matches.map(normalizeEmail).filter(Boolean));
  return [...uniq];
}

function getAccessEmailsFromServicePeople(userInputs: Record<string, string>, row: CSVRow): string[] {
  const targetFields = new Set(['responsable servicio', 'responsable', 'tecnico']);
  const emails = new Set<string>();

  Object.entries({ ...row, ...userInputs }).forEach(([key, value]) => {
    if (!targetFields.has(normalizeSearchText(key))) return;
    extractEmailsFromText(String(value || '')).forEach((email) => emails.add(email));
  });

  return [...emails];
}

function emailsFromUserFieldRaw(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return [];

  const directEmails = extractEmailsFromText(raw);
  if (directEmails.length) return directEmails;

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const emails = parsed
        .map((item: any) => item?.user_mail || item?.correo || item?.mail || item?.email || '')
        .map((v: string) => normalizeEmail(v))
        .filter(Boolean);
      return [...new Set(emails)];
    }
  } catch {
    return [];
  }
  return [];
}

function parseUserFieldNames(): string[] {
  const raw = (process.env.REACT_APP_SM_CSV_USER_FIELDS as string) || '';
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function collectDirectoryEntries(value: unknown, acc: DirectoryEntry[]) {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((item) => collectDirectoryEntries(item, acc));
    return;
  }
  if (typeof value !== 'object') return;

  const obj = value as Record<string, unknown>;
  const candidate = [
    obj.user_mail,
    obj.correo,
    obj.mail,
    obj.email,
    obj.upn,
    obj.UserPrincipalName,
  ]
    .map((v) => (typeof v === 'string' ? normalizeEmail(v) : ''))
    .find(Boolean);

  if (candidate) {
    const nameCandidate = [
      obj.nombre,
      obj.name,
      obj.displayName,
      obj.display_name,
      obj.NombreCompleto,
    ].find((v) => typeof v === 'string') as string | undefined;

    acc.push({ email: candidate, name: (nameCandidate || '').trim() });
  }

  Object.values(obj).forEach((v) => collectDirectoryEntries(v, acc));
}

function parseRoleValues(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((v) => String(v).trim().toUpperCase())
        .filter((v) => ROLE_OPTIONS.includes(v));
    }
  } catch {
    // fallback regex below
  }
  const matches = (raw.match(/[A-ZÁÉÍÓÚÜÑ]+/g) || []).map((v) => v.trim().toUpperCase());
  return [...new Set(matches.filter((v) => ROLE_OPTIONS.includes(v)))];
}

function parseMultiOptionValues(raw: string | undefined, options: string[]): string[] {
  if (!raw || !raw.trim()) return [];

  const normalizedOptions = new Map(options.map((option) => [normalizeSearchText(option), option]));
  return [
    ...new Set(
      raw
        .split(/[;,]/)
        .map((item) => normalizedOptions.get(normalizeSearchText(item)))
        .filter(Boolean) as string[]
    ),
  ];
}

function normalizeServicioValue(raw: string | undefined): string {
  const value = (raw || '').trim().toLowerCase();
  if (value === 'azure') return 'Azure';
  if (value === 'openai') return 'OpenAI';
  return '';
}

function toISODateInput(raw: string | undefined): string {
  if (!raw || !raw.trim()) return '';
  const value = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (!m) return '';
  const [, d, mo, y] = m;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function isImplementedState(value: string | undefined): boolean {
  return normalizeSearchText(value || '').includes('implementado');
}

function isDevelopmentState(value: string | undefined): boolean {
  return normalizeSearchText(value || '').includes('desarrollo');
}

function isCancelledState(value: string | undefined): boolean {
  return normalizeSearchText(value || '') === 'cancelado';
}

function getModifiedByColumn(columns: string[]): string | undefined {
  return columns.find((col) => normalizeSearchText(col) === 'modificado por');
}

function getModifiedDateColumn(columns: string[]): string | undefined {
  return columns.find((col) => normalizeSearchText(col) === 'modificado');
}

function getFechaColumn(columns: string[]): string | undefined {
  return columns.find((col) => normalizeSearchText(col) === 'fecha');
}

async function putPromptFile(url: string, content: string): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': 'text/plain; charset=utf-8',
    },
    body: content,
  });
  if (!res.ok) {
    throw new Error(`Error al crear prompt en blob (${res.status}).`);
  }
}

async function putTextFile(url: string, content: string, contentType: string): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
    },
    body: content,
  });
  if (!res.ok) {
    throw new Error(`Error al escribir blob (${res.status}).`);
  }
}

async function readTextFile(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Error al leer archivo base (${res.status}).`);
  }
  return res.text();
}

async function deleteBlobIfExists(url: string): Promise<void> {
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      'x-ms-delete-snapshots': 'include',
    },
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`Error al borrar blob (${res.status}).`);
  }
}

function sanitizeCsvFieldValue(value: unknown): string {
  return String(value ?? '').replace(/[\r\n]+/g, ' ');
}

function sanitizeCsvRow(row: CSVRow, fields: string[]): CSVRow {
  return fields.reduce((acc, field) => {
    acc[field] = sanitizeCsvFieldValue(row[field]);
    return acc;
  }, {} as CSVRow);
}

function unparseSemicolonCsv(rows: CSVRow[], fields: string[]): string {
  return Papa.unparse(
    rows.map((row) => sanitizeCsvRow(row, fields)),
    { delimiter: ';', columns: fields, newline: '\n' }
  );
}

function csvEscapeSemicolon(value: string): string {
  const str = sanitizeCsvFieldValue(value);
  if (str.includes(';') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function upsertServiceRowInCsv(
  csvText: string,
  record: Record<string, string>,
  fields: string[]
): string {
  if (!csvText.trim()) {
    const header = fields.join(';');
    const row = fields.map((field) => csvEscapeSemicolon(record[field] || '')).join(';');
    return `${header}\n${row}`;
  }

  const parsed = Papa.parse<CSVRow>(csvText, {
    header: true,
    delimiter: ';',
    skipEmptyLines: true,
  });

  const rows = parsed.data || [];
  const serviceId = normalizeSearchText(record['ID'] || '');
  const idx = rows.findIndex((r) => normalizeSearchText(String(r['ID'] || '')) === serviceId);

  const normalizedRecord = sanitizeCsvRow(record as CSVRow, fields);

  if (idx >= 0) rows[idx] = normalizedRecord;
  else rows.push(normalizedRecord);

  return unparseSemicolonCsv(rows, fields);
}

function removeServiceRowFromCsv(csvText: string, serviceId: string, fields: string[]): string {
  if (!csvText.trim()) return csvText;
  const parsed = Papa.parse<CSVRow>(csvText, {
    header: true,
    delimiter: ';',
    skipEmptyLines: true,
  });
  const rows = parsed.data || [];
  const normalizedId = normalizeSearchText(serviceId);
  const filteredRows = rows.filter((row) => normalizeSearchText(String(row['ID'] || '')) !== normalizedId);
  if (filteredRows.length === rows.length) return csvText;
  return unparseSemicolonCsv(filteredRows, fields);
}

function getAccessCsvEmail(row: Record<string, string>): string {
  const correoKey = Object.keys(row).find((key) => normalizeSearchText(key) === 'correo');
  return normalizeEmail(correoKey ? row[correoKey] : '');
}

function buildAccessCsvContent(emails: string[]): string {
  return Papa.unparse(
    emails.map((correo) => ({ correo })),
    { columns: ['correo'], newline: '\n' }
  );
}

async function ensureAccessCsvEmails(
  connectionString: string,
  containerName: string,
  emailsToEnsure: string[]
): Promise<void> {
  if (!emailsToEnsure.length) return;
  if (!containerName) {
    throw new Error('Falta REACT_APP_SM_BLOB_CONTAINER_NAME para actualizar accesos.');
  }

  const readUrl = await getBlobUrlByConnectionString(connectionString, containerName, ACCESS_CSV_BLOB_NAME, 'r');
  let currentEmails: string[] = [];
  const readRes = await fetch(readUrl);
  if (readRes.ok) {
    const text = await readRes.text();
    const parsed = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
    currentEmails = parsed.data.map(getAccessCsvEmail).filter(Boolean);
  } else if (readRes.status !== 404) {
    throw new Error(`Error leyendo CSV de accesos (${readRes.status}).`);
  }

  const knownEmails = new Set(currentEmails);
  const missingEmails = emailsToEnsure.filter((email) => !knownEmails.has(email));
  if (!missingEmails.length) return;

  const updatedEmails = [...currentEmails, ...missingEmails];
  const writeUrl = await getBlobUrlByConnectionString(connectionString, containerName, ACCESS_CSV_BLOB_NAME, 'rw');
  await putTextFile(writeUrl, buildAccessCsvContent(updatedEmails), 'text/csv; charset=utf-8');
}

function buildServiceCsvRecord(row: CSVRow, userInputs: Record<string, string>, currentUserEmail: string): Record<string, string> {
  const usuarioCorreo =
    extractEmailsFromText(userInputs['Usuario'] || '')[0] ||
    parseUserEntries(row['Usuario'])[0]?.email ||
    currentUserEmail ||
    '';
  const roleValue = parseRoleValues(row['Role']).join(',');

  return {
    NombreProceso: getFieldValue(row, ['NombreProceso', 'Nombre del servicio']),
    descripcion: getFieldValue(row, ['descripcion', 'Descripción']),
    ID: getFieldValue(row, ['ID']),
    Centro: getFieldValue(row, ['Centro']),
    Role: roleValue,
    Whatsapp: getFieldValue(row, ['Whatsapp']),
    Usuario: usuarioCorreo,
    Fecha: getFieldValue(row, ['Fecha']),
    Sinidentificacion: getFieldValue(row, ['Sinidentificacion']),
    'Fecha novedad proceso': getFieldValue(row, ['Fecha novedad proceso', 'Fecha_novedad_proceso']),
    'Estado implementación': getFieldValue(row, ['Estado implementación', 'Estado_implementacion']),
  };
}

type EditorCsvTab = 'desarrollo' | 'produccion';

/** Aplica los campos del CSV de servicio sobre el draft del editor. */
function applyServiceCsvRecordToDraft(base: CSVRow, csvRecord: Record<string, string> | null): CSVRow {
  if (!csvRecord) return { ...base };
  const next = { ...base };

  const nombre = String(csvRecord['NombreProceso'] || '').trim();
  if (nombre) {
    next['NombreProceso'] = nombre;
    next['Nombre del servicio'] = nombre;
  }

  const descripcion = String(csvRecord['descripcion'] || '').trim();
  if (descripcion) {
    next['descripcion'] = descripcion;
    next['Descripción'] = descripcion;
  }

  const id = String(csvRecord['ID'] || '').trim();
  if (id) next['ID'] = id;

  const centro = String(csvRecord['Centro'] || '').trim();
  if (centro) next['Centro'] = centro;

  const whatsapp = String(csvRecord['Whatsapp'] || '').trim();
  if (whatsapp) next['Whatsapp'] = whatsapp;

  const fecha = String(csvRecord['Fecha'] || '').trim();
  if (fecha) next['Fecha'] = fecha;

  const sinIdent = String(csvRecord['Sinidentificacion'] || '').trim();
  if (sinIdent) next['Sinidentificacion'] = sinIdent;

  const fechaNovedad = String(csvRecord['Fecha novedad proceso'] || csvRecord['Fecha_novedad_proceso'] || '').trim();
  if (fechaNovedad) {
    next['Fecha novedad proceso'] = fechaNovedad;
    next['Fecha_novedad_proceso'] = fechaNovedad;
  }

  const estado = String(csvRecord['Estado implementación'] || csvRecord['Estado_implementacion'] || '').trim();
  if (estado) {
    next['Estado implementación'] = estado;
    next['Estado_implementacion'] = estado;
  }

  const roleRaw = String(csvRecord['Role'] || '').trim();
  if (roleRaw) {
    const roles = roleRaw.split(',').map((item) => item.trim()).filter(Boolean);
    next['Role'] = JSON.stringify(roles);
  }

  const usuario = String(csvRecord['Usuario'] || '').trim();
  if (usuario) {
    next['Usuario'] = JSON.stringify([{ nombre: '', user_mail: normalizeEmail(usuario) }]);
  }

  return next;
}

function buildUserFieldInputsFromRow(row: CSVRow, userFields: Set<string>, columns: string[]): Record<string, string> {
  const nextUserInputs: Record<string, string> = {};
  columns.forEach((col) => {
    if (!userFields.has(col)) return;
    nextUserInputs[col] = emailsFromUserFieldRaw(row[col]).join(', ');
  });
  return nextUserInputs;
}

function buildRoleFieldInputsFromRow(row: CSVRow, columns: string[]): Record<string, string[]> {
  const nextRoleInputs: Record<string, string[]> = {};
  columns.forEach((col) => {
    if (col.toLowerCase() !== 'role') return;
    nextRoleInputs[col] = parseRoleValues(row[col]);
  });
  return nextRoleInputs;
}

/** Extrae los nombres de un campo JSON y los une en un string legible */
function parseUsers(raw: string | undefined): string {
  if (!raw || !raw.trim()) return '-';
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      const names = arr
        .map((item: { nombre?: string; user_mail?: string }) => {
          if (item.nombre && item.nombre.trim() !== '') return item.nombre;
          return item.user_mail || '';
        })
        .filter(Boolean);
      return names.length > 0 ? names.join(', ') : '-';
    }
  } catch {
    return raw; // fallback safe
  }
  return '-';
}

/** Color del badge de estado */
function estadoClass(estado: string | undefined): string {
  if (!estado) return 'estado-default';
  const lower = estado.toLowerCase().trim();
  if (lower.includes('implementado') || lower.includes('producción') || lower.includes('produccion') || lower.includes('activo') || lower.includes('completado'))
    return 'estado-activo';
  if (lower.includes('desarrollo') || lower.includes('progreso') || lower.includes('implementa'))
    return 'estado-desarrollo';
  if (lower.includes('pendiente') || lower.includes('parado') || lower.includes('pausa'))
    return 'estado-pendiente';
  return 'estado-default';
}

function taskStatusLabel(status: ServiceTask['estado']): string {
  if (status === 'in_progress') return 'En curso';
  if (status === 'completed') return 'Completada';
  return 'Pendiente';
}

function taskStatusClass(status: ServiceTask['estado']): string {
  if (status === 'in_progress') return 'is-progress';
  if (status === 'completed') return 'is-completed';
  return 'is-pending';
}

function taskStatusIcon(status: ServiceTask['estado']) {
  if (status === 'in_progress') return <Clock size={14} />;
  if (status === 'completed') return <CheckCircle2 size={14} />;
  return <AlertCircle size={14} />;
}

interface ServiceInitiativeDetailModalProps {
  row: CSVRow;
  areas: ServiceInitiativeArea[];
  onClose: () => void;
  onRowUpdated: (row: CSVRow) => void;
  onBeforeStateChange?: (row: CSVRow, nextState: string) => Promise<void>;
}

const END_WEEK_MONTHS = ['Nov', 'Dic', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct'];
const END_WEEK_NUMBERS = ['1', '2', '3', '4', '5'];

const ServiceInitiativeDetailModal: React.FC<ServiceInitiativeDetailModalProps> = ({ row, areas, onClose, onRowUpdated, onBeforeStateChange }) => {
  const estado = getServiceEstado(row);
  const favorite = isFavorite(row);
  const responsables = parseUserEntries(row['Responsable servicio']);
  const tecnicos = parseUserEntries(row['Técnico']);
  const revisores = parseUserEntries(row['Revisor/a'] || row['Revisor']);
  const roles = parseRoleLabels(row['Role'] || row['ROL']);
  const modifiedRaw = getModifiedDateValue(row);
  const modifiedDate = parseModifiedDate(modifiedRaw);
  const displayModified = modifiedDate
    ? modifiedDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
    : modifiedRaw;
  const initiativeId = row.__database_id;
  const [tasks, setTasks] = useState<ServiceTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [savingTaskId, setSavingTaskId] = useState<string | null>(null);
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskAssigneeEmail, setNewTaskAssigneeEmail] = useState('');
  const [newTaskEndWeekMonth, setNewTaskEndWeekMonth] = useState('');
  const [newTaskEndWeekNumber, setNewTaskEndWeekNumber] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);
  const [statusDescriptions, setStatusDescriptions] = useState<ServiceStatusDescription[]>([]);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [statusDraft, setStatusDraft] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [savingInitiativeState, setSavingInitiativeState] = useState(false);
  const [savingArea, setSavingArea] = useState(false);
  const assignablePeople = useMemo(() => {
    const byEmail = new Map<string, UserEntry>();
    [...tecnicos, ...responsables].forEach((person) => {
      const key = person.email || person.name;
      if (key && !byEmail.has(key)) byEmail.set(key, person);
    });
    return [...byEmail.values()];
  }, [tecnicos, responsables]);
  const latestStatus = statusDescriptions[0] || null;
  const detailAreaOptions = useMemo(() => {
    const normalized = areas
      .map((area) => ({
        code: area.codigo.toUpperCase().trim(),
        label: area.descripcion.trim() || area.codigo,
      }))
      .filter((area) => area.code);
    const currentCode = (row.__codigo_area || '').toUpperCase().trim();
    if (currentCode && !normalized.some((area) => area.code === currentCode)) {
      normalized.push({ code: currentCode, label: currentCode });
    }
    return normalized;
  }, [areas, row.__codigo_area]);

  useEffect(() => {
    let cancelled = false;
    const loadDetailData = async () => {
      if (!initiativeId) {
        setTasks([]);
        setStatusDescriptions([]);
        return;
      }
      try {
        setTasksLoading(true);
        setTasksError(null);
        setStatusError(null);
        const [nextTasks, nextStatuses] = await Promise.all([
          fetchServiceTasks(initiativeId),
          fetchServiceStatusDescriptions(initiativeId),
        ]);
        if (!cancelled) {
          setTasks(nextTasks);
          setStatusDescriptions(nextStatuses);
        }
      } catch (err) {
        if (!cancelled) setTasksError(err instanceof Error ? err.message : 'No se pudieron cargar las tareas.');
      } finally {
        if (!cancelled) setTasksLoading(false);
      }
    };
    loadDetailData();
    return () => {
      cancelled = true;
    };
  }, [initiativeId]);

  const updateTask = async (taskId: string, updates: Partial<ServiceTask>) => {
    try {
      setSavingTaskId(taskId);
      setTasksError(null);
      const saved = await updateServiceTask(taskId, updates);
      setTasks((prev) => prev.map((task) => (task.id === taskId ? saved : task)));
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : 'No se pudo actualizar la tarea.');
    } finally {
      setSavingTaskId(null);
    }
  };

  const createTask = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!initiativeId || !newTaskTitle.trim()) return;
    const person = assignablePeople.find((item) => item.email === newTaskAssigneeEmail);
    const endWeekLabel = newTaskEndWeekMonth && newTaskEndWeekNumber
      ? `${newTaskEndWeekMonth}-${newTaskEndWeekNumber}`
      : null;

    try {
      setCreatingTask(true);
      setTasksError(null);
      const created = await createServiceTask({
        initiative_id: initiativeId,
        nombre_iniciativa: getServiceName(row) || null,
        titulo: newTaskTitle.trim(),
        descripcion: newTaskDescription.trim() || null,
        asignado_a: person?.name || null,
        asignado_email: person?.email || null,
        semana_fin: endWeekLabel,
        estado: 'pending',
        legacy_initiative_id: row.ID || null,
      });
      setTasks((prev) => [created, ...prev]);
      setNewTaskTitle('');
      setNewTaskDescription('');
      setNewTaskAssigneeEmail('');
      setNewTaskEndWeekMonth('');
      setNewTaskEndWeekNumber('');
      setShowNewTaskForm(false);
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : 'No se pudo crear la tarea.');
    } finally {
      setCreatingTask(false);
    }
  };

  const saveStatusDescription = async () => {
    if (!initiativeId || !statusDraft.trim()) return;
    try {
      setStatusSaving(true);
      setStatusError(null);
      const created = await createServiceStatusDescription(initiativeId, statusDraft.trim());
      setStatusDescriptions((prev) => [created, ...prev]);
      setStatusDraft('');
      setShowStatusForm(false);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'No se pudo guardar el estado actual.');
    } finally {
      setStatusSaving(false);
    }
  };

  const updateInitiativeState = async (nextState: string) => {
    if (!nextState || nextState === estado) return;
    try {
      setSavingInitiativeState(true);
      setStatusError(null);
      await onBeforeStateChange?.(row, nextState);
      const saved = await upsertServiceInitiativeRow({
        ...row,
        Estado_implementacion: nextState,
        'Estado implementación': nextState,
      });
      onRowUpdated(saved);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'No se pudo actualizar el estado de la iniciativa.');
    } finally {
      setSavingInitiativeState(false);
    }
  };

  const updateInitiativeArea = async (nextAreaCode: string) => {
    const normalizedAreaCode = nextAreaCode.toUpperCase().trim();
    if (normalizedAreaCode === (row.__codigo_area || '').toUpperCase().trim()) return;
    try {
      setSavingArea(true);
      setStatusError(null);
      const saved = await upsertServiceInitiativeRow({
        ...row,
        __codigo_area: normalizedAreaCode,
      });
      onRowUpdated(saved);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'No se pudo actualizar el área de la iniciativa.');
    } finally {
      setSavingArea(false);
    }
  };

  const peopleSection = (title: string, icon: React.ReactNode, people: UserEntry[]) => (
    <section className="sm-detail-people-block">
      <div className="sm-detail-section-title">
        {icon}
        <h3>{title}</h3>
      </div>
      {people.length > 0 ? (
        <div className="sm-detail-people-list">
          {people.map((person, index) => (
            <div key={`${title}-${person.email || person.name}-${index}`} className="sm-detail-person">
              <span className="sm-detail-person-avatar">{(person.name || person.email || '?').slice(0, 1).toUpperCase()}</span>
              <span className="sm-detail-person-text">
                <strong>{person.name || person.email}</strong>
                {person.email && <small>{person.email}</small>}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="sm-detail-empty">Sin datos registrados</p>
      )}
    </section>
  );

  return (
    <div className="sm-detail-overlay" role="dialog" aria-modal="true" aria-labelledby="sm-detail-title" onClick={onClose}>
      <article className="sm-detail-modal" onClick={(e) => e.stopPropagation()}>
        <header className="sm-detail-header">
          <div className="sm-detail-header-main">
            <div className="sm-detail-kicker">
              <span className={`lp-badge lp-badge-estado ${estadoClass(estado)}`}>
                <span className="lp-badge-dot" />
                {estado || 'Sin estado'}
              </span>
              {favorite && (
                <span className="sm-detail-favorite">
                  <Star size={14} fill="currentColor" />
                  Favorita
                </span>
              )}
            </div>
            <h2 id="sm-detail-title">{getServiceName(row) || 'Servicio sin nombre'}</h2>
            <div className="sm-detail-header-meta">
              {row['Centro'] && <span>{row['Centro']}</span>}
              {row['Funcionalidad'] && <span>{normalizeFuncionalidadValue(row['Funcionalidad'])}</span>}
              {row.__codigo_area && <span>Área {row.__codigo_area}</span>}
            </div>
          </div>
          <button type="button" className="sm-detail-close" onClick={onClose} aria-label="Cerrar detalle">
            <X size={20} />
          </button>
        </header>

        <div className="sm-detail-body">
          <section className="sm-detail-summary">
            <div className="sm-detail-field">
              <span>Estado</span>
              <select
                className="sm-detail-state-select"
                value={estado || ''}
                disabled={savingInitiativeState}
                onChange={(event) => updateInitiativeState(event.target.value)}
                aria-label="Estado de implementación"
              >
                <option value="">Sin estado</option>
                {ESTADO_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="sm-detail-field">
              <span>Área</span>
              <select
                className="sm-detail-state-select"
                value={(row.__codigo_area || '').toUpperCase().trim()}
                disabled={savingArea}
                onChange={(event) => updateInitiativeArea(event.target.value)}
                aria-label="Área de la iniciativa"
              >
                <option value="">Sin área</option>
                {detailAreaOptions.map((area) => (
                  <option key={area.code} value={area.code}>{area.label}</option>
                ))}
              </select>
            </div>
            <div className="sm-detail-field">
              <span>Centro</span>
              <strong>{row['Centro'] || '-'}</strong>
            </div>
            <div className="sm-detail-field">
              <span>Funcionalidad</span>
              <strong>{normalizeFuncionalidadValue(row['Funcionalidad']) || '-'}</strong>
            </div>
            <div className="sm-detail-field">
              <span>Última modificación</span>
              <strong>{displayModified || '-'}</strong>
            </div>
            <div className="sm-detail-field sm-detail-field-muted">
              <span>ID</span>
              <strong>{row['ID'] || '-'}</strong>
            </div>
          </section>

          {roles.length > 0 && (
            <section className="sm-detail-section">
              <div className="sm-detail-section-title">
                <Users size={16} />
                <h3>Roles</h3>
              </div>
              <div className="sm-detail-tags">
                {roles.map((role) => (
                  <span key={role} className="lp-role-chip">{role}</span>
                ))}
              </div>
            </section>
          )}

          <section className="sm-detail-section">
            <div className="sm-detail-section-title">
              <Activity size={16} />
              <h3>Descripción</h3>
            </div>
            <p className="sm-detail-description">{row['descripcion'] || row['Descripción'] || 'Sin descripción registrada.'}</p>
          </section>

          <section className="sm-detail-section sm-detail-status-section">
            <div className="sm-detail-section-title sm-detail-section-title-row">
              <span className="sm-detail-section-title-left">
                <MessageSquarePlus size={16} />
                <h3>Estado actual</h3>
              </span>
              <button
                type="button"
                className="sm-detail-small-btn"
                onClick={() => {
                  setShowStatusForm((prev) => !prev);
                  setStatusDraft('');
                }}
              >
                <Plus size={14} />
                Nueva descripción
              </button>
            </div>
            {statusError && (
              <div className="error-alert sm-detail-task-error">
                <AlertCircle size={16} />
                {statusError}
              </div>
            )}
            {showStatusForm && (
              <div className="sm-detail-status-form">
                <textarea
                  value={statusDraft}
                  onChange={(event) => setStatusDraft(event.target.value)}
                  placeholder="Describe el estado actual de esta iniciativa..."
                />
                <div className="sm-detail-form-actions">
                  <button
                    type="button"
                    className="sm-detail-action-primary"
                    disabled={statusSaving || !statusDraft.trim()}
                    onClick={saveStatusDescription}
                  >
                    {statusSaving ? 'Guardando...' : 'Guardar'}
                  </button>
                  <button
                    type="button"
                    className="sm-detail-action-secondary"
                    onClick={() => {
                      setShowStatusForm(false);
                      setStatusDraft('');
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
            {latestStatus ? (
              <div className="sm-detail-current-status">
                <p>{latestStatus.description}</p>
                <span>
                  <Clock size={13} />
                  {new Date(latestStatus.created_at).toLocaleString('es-ES', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            ) : (
              <p className="sm-detail-empty">Sin descripción de estado todavía.</p>
            )}
          </section>

          <div className="sm-detail-people-grid">
            {peopleSection('Responsables', <Users size={16} />, responsables)}
            {peopleSection('Técnicos', <User size={16} />, tecnicos)}
            {peopleSection('Revisión', <AlertCircle size={16} />, revisores)}
          </div>

          <section className="sm-detail-section sm-detail-tasks-section">
            <div className="sm-detail-section-title sm-detail-section-title-row">
              <span className="sm-detail-section-title-left">
                <CheckCircle2 size={16} />
                <h3>Tareas</h3>
              </span>
              <button type="button" className="sm-detail-small-btn" onClick={() => setShowNewTaskForm((prev) => !prev)}>
                <Plus size={14} />
                Nueva tarea
              </button>
            </div>
            {tasksError && (
              <div className="error-alert sm-detail-task-error">
                <AlertCircle size={16} />
                {tasksError}
              </div>
            )}
            {showNewTaskForm && (
              <form className="sm-detail-new-task-form" onSubmit={createTask}>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(event) => setNewTaskTitle(event.target.value)}
                  placeholder="Título de la tarea"
                  required
                />
                <textarea
                  value={newTaskDescription}
                  onChange={(event) => setNewTaskDescription(event.target.value)}
                  placeholder="Descripción breve (opcional)"
                />
                <div className="sm-detail-new-task-grid">
                  <select value={newTaskAssigneeEmail} onChange={(event) => setNewTaskAssigneeEmail(event.target.value)}>
                    <option value="">Sin asignar</option>
                    {assignablePeople.map((person) => (
                      <option key={person.email || person.name} value={person.email}>
                        {person.name || person.email}
                      </option>
                    ))}
                  </select>
                  <select value={newTaskEndWeekMonth} onChange={(event) => setNewTaskEndWeekMonth(event.target.value)}>
                    <option value="">Mes fin</option>
                    {END_WEEK_MONTHS.map((month) => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                  <select value={newTaskEndWeekNumber} onChange={(event) => setNewTaskEndWeekNumber(event.target.value)}>
                    <option value="">Semana</option>
                    {END_WEEK_NUMBERS.map((week) => (
                      <option key={week} value={week}>{week}</option>
                    ))}
                  </select>
                </div>
                <div className="sm-detail-form-actions">
                  <button type="submit" className="sm-detail-action-primary" disabled={creatingTask || !newTaskTitle.trim()}>
                    {creatingTask ? 'Creando...' : 'Crear tarea'}
                  </button>
                  <button type="button" className="sm-detail-action-secondary" onClick={() => setShowNewTaskForm(false)}>
                    Cancelar
                  </button>
                </div>
              </form>
            )}
            {tasksLoading ? (
              <div className="sm-detail-tasks-loading">Cargando tareas...</div>
            ) : tasks.length === 0 ? (
              <p className="sm-detail-empty">Sin tareas registradas.</p>
            ) : (
              <div className="sm-detail-tasks-list">
                {tasks.map((task) => (
                  <article key={task.id} className="sm-detail-task">
                    <div className="sm-detail-task-main">
                      <div className={`sm-detail-task-status ${taskStatusClass(task.estado)}`}>
                        {taskStatusIcon(task.estado)}
                        {taskStatusLabel(task.estado)}
                      </div>
                      <div className="sm-detail-task-copy">
                        <h4>{task.titulo}</h4>
                        {task.descripcion && <p>{task.descripcion}</p>}
                        <div className="sm-detail-task-meta">
                          {task.asignado_a && <span>{task.asignado_a}</span>}
                          {task.semana_fin && (
                            <span>
                              <Calendar size={13} />
                              {task.semana_fin}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="sm-detail-task-actions">
                      <select
                        value={task.estado}
                        disabled={savingTaskId === task.id}
                        onChange={(event) => updateTask(task.id, { estado: event.target.value as ServiceTask['estado'] })}
                        aria-label={`Estado de ${task.titulo}`}
                      >
                        <option value="pending">Pendiente</option>
                        <option value="in_progress">En curso</option>
                        <option value="completed">Completada</option>
                      </select>
                      <select
                        value={task.asignado_email || ''}
                        disabled={savingTaskId === task.id || assignablePeople.length === 0}
                        onChange={(event) => {
                          const person = assignablePeople.find((item) => item.email === event.target.value);
                          updateTask(task.id, {
                            asignado_a: person?.name || null,
                            asignado_email: person?.email || null,
                          });
                        }}
                        aria-label={`Asignación de ${task.titulo}`}
                      >
                        <option value="">Sin asignar</option>
                        {assignablePeople.map((person) => (
                          <option key={person.email || person.name} value={person.email}>
                            {person.name || person.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </article>
    </div>
  );
};

const ListPage: React.FC = () => {
  const { instance } = useMsal();
  const { data, columns, loading, error } = useCSVData();
  const [rows, setRows] = useState<CSVRow[]>([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('TODOS');
  const [selectedAreaCode, setSelectedAreaCode] = useState('TODAS');
  const [areas, setAreas] = useState<ServiceInitiativeArea[]>([]);
  const [filterEstado, setFilterEstado] = useState('VISIBLES');
  const [modifiedSortOrder, setModifiedSortOrder] = useState<'desc' | 'asc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [intakeChatCollapsed, setIntakeChatCollapsed] = useState(false);

  const [page, setPage] = useState(1);
  const rowsPerPage = 24;

  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [selectedDetailRow, setSelectedDetailRow] = useState<CSVRow | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [draftRow, setDraftRow] = useState<CSVRow | null>(null);
  const [savingRow, setSavingRow] = useState(false);
  const [deletingRow, setDeletingRow] = useState(false);
  const [savingFavoriteIds, setSavingFavoriteIds] = useState<Set<string>>(new Set());
  const [modalMessage, setModalMessage] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [directoryEntries, setDirectoryEntries] = useState<DirectoryEntry[]>([]);
  const [databaseUserEntries, setDirectoryUserEntries] = useState<DirectoryEntry[]>([]);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [userFieldInputs, setUserFieldInputs] = useState<Record<string, string>>({});
  const [roleFieldInputs, setRoleFieldInputs] = useState<Record<string, string[]>>({});
  const [activeUserField, setActiveUserField] = useState<string | null>(null);
  const [editorCsvTab, setEditorCsvTab] = useState<EditorCsvTab>('desarrollo');
  const [showCsvEnvTabs, setShowCsvEnvTabs] = useState(false);
  const [draftDes, setDraftDes] = useState<CSVRow | null>(null);
  const [draftProd, setDraftProd] = useState<CSVRow | null>(null);
  const [userFieldInputsDes, setUserFieldInputsDes] = useState<Record<string, string>>({});
  const [userFieldInputsProd, setUserFieldInputsProd] = useState<Record<string, string>>({});
  const [roleFieldInputsDes, setRoleFieldInputsDes] = useState<Record<string, string[]>>({});
  const [roleFieldInputsProd, setRoleFieldInputsProd] = useState<Record<string, string[]>>({});
  const [csvEnvLoading, setCsvEnvLoading] = useState(false);
  const [csvEnvMissing, setCsvEnvMissing] = useState<{ desarrollo: boolean; produccion: boolean }>({
    desarrollo: false,
    produccion: false,
  });
  const userFieldNames = useMemo(() => parseUserFieldNames(), []);
  const userFieldSet = useMemo(() => new Set(userFieldNames), [userFieldNames]);
  const directoryNameByEmail = useMemo(() => {
    return buildNameByEmail(directoryEntries, databaseUserEntries);
  }, [directoryEntries, databaseUserEntries]);
  const userLookupEntries = useMemo(() => {
    const byEmail = new Map<string, DirectoryEntry>();
    directoryEntries.forEach((entry) => byEmail.set(entry.email, entry));
    databaseUserEntries.forEach((entry) => byEmail.set(entry.email, entry));
    return [...byEmail.values()].sort((a, b) => a.email.localeCompare(b.email));
  }, [directoryEntries, databaseUserEntries]);
  const formColumns = useMemo(() => {
    const base = [...columns];
    const servicioIndex = base.findIndex((col) => normalizeSearchText(col) === 'servicio');
    if (servicioIndex !== -1) {
      base.splice(servicioIndex, 1);
    }
    // Posición 7 (1-based) => índice 6 (0-based)
    const targetIndex = Math.min(6, base.length);
    base.splice(targetIndex, 0, 'servicio');
    return base;
  }, [columns]);
  const activeAccount = instance.getActiveAccount();
  const currentUserEmail = (activeAccount?.username || '').trim();
  const modifiedByColumn = useMemo(() => getModifiedByColumn(columns), [columns]);
  const modifiedDateColumn = useMemo(() => getModifiedDateColumn(columns), [columns]);
  const fechaColumn = useMemo(() => getFechaColumn(columns), [columns]);

  const cleanConnectionString = (value: string | undefined): string =>
    String(value || '').trim().replace(/^["']+|["']+$/g, '');
  const secondaryBlobConnectionString = cleanConnectionString(
    process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING
  );
  const primaryBlobConnectionString = cleanConnectionString(
    process.env.REACT_APP_SM_BLOB_CONNECTION_STRING
  );
  const promptsContainerName = (process.env.REACT_APP_SM_PROMPTS_CONTAINER_NAME as string) || 'promptsprocesos';
  const promptsFolderName = (process.env.REACT_APP_SM_PROMPTS_FOLDER_NAME as string) || 'desarrollo';
  const promptsDeployedFolderName = (process.env.REACT_APP_SM_PROMPTS_DEPLOYED_FOLDER_NAME as string) || 'desplegado';
  const createServiceCsvContainer = (process.env.REACT_APP_SM_CREATE_SERVICE_CSV_CONTAINER as string) || 'promptsprocesos';
  const createServiceCsvFile = (process.env.REACT_APP_SM_CREATE_SERVICE_CSV_FILE as string) || 'ListaProcesosServiciosGPT_CEU_des.csv';
  const deployServiceCsvFile = (process.env.REACT_APP_SM_DEPLOY_SERVICE_CSV_FILE as string) || 'ListaProcesosServiciosGPT_CEU.csv';
  const createServiceConfigContainer = (process.env.REACT_APP_SM_CREATE_SERVICE_CONFIG_CONTAINER as string) || 'configuraciones';
  const accessCsvContainerName = process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;

  const getConfiguredBlobConnectionStrings = () => {
    return [primaryBlobConnectionString, secondaryBlobConnectionString].filter(Boolean);
  };

  const requireBothBlobConnectionStrings = () => {
    if (!primaryBlobConnectionString || !secondaryBlobConnectionString) {
      throw new Error('Faltan REACT_APP_SM_BLOB_CONNECTION_STRING o REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING.');
    }
  };

  /** Upsert del registro de servicio en un CSV concreto de una cuenta blob. */
  const upsertServiceRecordInCsvFile = async (
    connectionString: string,
    csvFileName: string,
    record: Record<string, string>
  ) => {
    const csvReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, csvFileName, 'r');
    let csvText = '';
    const readRes = await fetch(csvReadUrl);
    if (readRes.ok) {
      csvText = await readRes.text();
    } else if (readRes.status !== 404) {
      throw new Error(`Error al leer ${csvFileName} (${readRes.status}).`);
    }

    const updatedCsv = upsertServiceRowInCsv(csvText, record, CREATE_SERVICE_CSV_FIELDS);
    const csvWriteUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, csvFileName, 'rw');
    await putTextFile(csvWriteUrl, updatedCsv, 'text/csv; charset=utf-8');
  };

  /** Lee un registro de servicio por ID (o NombreProceso) desde un CSV de blob. */
  const readServiceRecordFromCsv = async (
    connectionString: string,
    csvFileName: string,
    serviceId: string,
    processName?: string
  ): Promise<Record<string, string> | null> => {
    if (!connectionString) {
      throw new Error(`Falta connection string para leer ${csvFileName}.`);
    }
    if (!serviceId && !processName) return null;

    const csvReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, csvFileName, 'r');
    const readRes = await fetch(csvReadUrl);
    if (readRes.status === 404) return null;
    if (!readRes.ok) {
      throw new Error(`Error al leer ${csvFileName} (${readRes.status}).`);
    }

    const csvText = (await readRes.text()).replace(/^\uFEFF/, '');
    if (!csvText.trim()) return null;

    const parsed = Papa.parse<CSVRow>(csvText, {
      header: true,
      delimiter: ';',
      skipEmptyLines: true,
    });
    const rows = parsed.data || [];

    const getCsvCell = (row: CSVRow, candidates: string[]): string => {
      for (const candidate of candidates) {
        const direct = row[candidate];
        if (typeof direct === 'string' && direct.trim()) return direct.trim();
      }
      const normalizedCandidates = candidates.map((item) => normalizeSearchText(item));
      const key = Object.keys(row).find((header) =>
        normalizedCandidates.includes(normalizeSearchText(header))
      );
      return key ? String(row[key] || '').trim() : '';
    };

    const normalizedId = normalizeSearchText(serviceId || '');
    const normalizedName = normalizeSearchText(processName || '');

    let match =
      (normalizedId
        ? rows.find((row) => normalizeSearchText(getCsvCell(row, ['ID'])) === normalizedId)
        : undefined) ||
      (normalizedName
        ? rows.find((row) => normalizeSearchText(getCsvCell(row, ['NombreProceso', 'Nombre del servicio'])) === normalizedName)
        : undefined);

    if (!match) return null;

    return CREATE_SERVICE_CSV_FIELDS.reduce((acc, field) => {
      acc[field] = sanitizeCsvFieldValue(
        getCsvCell(match as CSVRow, [field, field.replace(/ /g, '_')])
      );
      return acc;
    }, {} as Record<string, string>);
  };

  /** CSV producción: siempre REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING + deploy file. */
  const readProductionServiceRecord = async (serviceId: string, processName?: string) => {
    if (!secondaryBlobConnectionString) {
      throw new Error('Falta REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING para leer el CSV de producción.');
    }
    return readServiceRecordFromCsv(
      secondaryBlobConnectionString,
      deployServiceCsvFile,
      serviceId,
      processName
    );
  };

  /** CSV desarrollo: siempre REACT_APP_SM_BLOB_CONNECTION_STRING + des file. */
  const readDevelopmentServiceRecord = async (serviceId: string, processName?: string) => {
    if (!primaryBlobConnectionString) {
      throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING para leer el CSV de desarrollo.');
    }
    return readServiceRecordFromCsv(
      primaryBlobConnectionString,
      createServiceCsvFile,
      serviceId,
      processName
    );
  };

  /**
   * Si el servicio está implementado, el registro debe quedar en:
   * - CSV desarrollo (REACT_APP_SM_BLOB_CONNECTION_STRING / *_des.csv)
   * - CSV producción (REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING / ListaProcesosServiciosGPT_CEU.csv)
   * y se espeja en ambas cuentas.
   */
  const syncImplementedServiceCsvRecords = async (record: Record<string, string>) => {
    requireBothBlobConnectionStrings();
    await Promise.all([
      upsertServiceRecordInCsvFile(primaryBlobConnectionString, createServiceCsvFile, record),
      upsertServiceRecordInCsvFile(secondaryBlobConnectionString, createServiceCsvFile, record),
      upsertServiceRecordInCsvFile(primaryBlobConnectionString, deployServiceCsvFile, record),
      upsertServiceRecordInCsvFile(secondaryBlobConnectionString, deployServiceCsvFile, record),
    ]);
  };

  /** Elimina el registro de un CSV concreto en una cuenta blob. */
  const removeServiceRecordFromCsvFile = async (
    connectionString: string,
    csvFileName: string,
    serviceId: string
  ) => {
    const csvReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, csvFileName, 'r');
    const readRes = await fetch(csvReadUrl);
    if (readRes.status === 404) return;
    if (!readRes.ok) {
      throw new Error(`Error al leer ${csvFileName} (${readRes.status}).`);
    }

    const csvText = await readRes.text();
    const updatedCsv = removeServiceRowFromCsv(csvText, serviceId, CREATE_SERVICE_CSV_FIELDS);
    if (updatedCsv === csvText) return;

    const csvWriteUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, csvFileName, 'rw');
    await putTextFile(csvWriteUrl, updatedCsv, 'text/csv; charset=utf-8');
  };

  /** Al volver a desarrollo, el servicio debe salir del CSV de producción en ambas cuentas. */
  const removeServiceFromProductionCsv = async (serviceId: string) => {
    if (!serviceId) {
      throw new Error('No se puede quitar del CSV de producción: falta ID.');
    }
    requireBothBlobConnectionStrings();
    await Promise.all([
      removeServiceRecordFromCsvFile(primaryBlobConnectionString, deployServiceCsvFile, serviceId),
      removeServiceRecordFromCsvFile(secondaryBlobConnectionString, deployServiceCsvFile, serviceId),
    ]);
  };

  const readServiceConfigFromBlob = async (connectionString: string, serviceId: string): Promise<Record<string, any> | null> => {
    const configBlobName = `${serviceId}.json`;
    const configReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceConfigContainer, configBlobName, 'r');
    const configReadRes = await fetch(configReadUrl);
    if (configReadRes.status === 404) return null;
    if (!configReadRes.ok) {
      throw new Error(`Error al leer configuración ${configBlobName} (${configReadRes.status}).`);
    }
    return configReadRes.json();
  };

  const deleteStoredBlobUrlForConnectionString = async (connectionString: string, storedBlobUrl: string) => {
    if (!storedBlobUrl) return;
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(storedBlobUrl);
    } catch {
      return;
    }
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    const [containerName, ...blobPathSegments] = pathSegments;
    if (!containerName || !blobPathSegments.length) return;

    const blobName = blobPathSegments.map((segment) => decodeURIComponent(segment)).join('/');
    const deleteUrl = await getBlobUrlByConnectionString(connectionString, containerName, blobName, 'd');
    await deleteBlobIfExists(deleteUrl);
  };

  const cleanupGptCeuServiceFromAzure = async (row: CSVRow) => {
    const serviceId = getFieldValue(row, ['ID']);
    const processName = getFieldValue(row, ['NombreProceso', 'Nombre del servicio']);
    const connectionStrings = getConfiguredBlobConnectionStrings();

    if (!connectionStrings.length) {
      throw new Error('No hay connection strings configurados para eliminar en Azure.');
    }

    const vectorStoreIds = new Set<string>();
    const azureFileIds = new Set<string>();
    const documentBlobUrls = new Set<string>();
    const promptFileNames = new Set<string>();

    const deleteForConnectionString = async (connectionString: string) => {
      let existingConfig: Record<string, any> | null = null;

      if (serviceId) {
        existingConfig = await readServiceConfigFromBlob(connectionString, serviceId);
        const vectorStoreId = String(existingConfig?.vector_storage_id || '').trim();
        if (vectorStoreId) vectorStoreIds.add(vectorStoreId);

        const documents = Array.isArray(existingConfig?.Documentos) ? existingConfig.Documentos : [];
        documents.forEach((doc: any) => {
          const blobUrl = String(doc?.rutaBlob || '').trim();
          const azureFileId = String(doc?.azureFileId || '').trim();
          if (blobUrl) documentBlobUrls.add(blobUrl);
          if (azureFileId) azureFileIds.add(azureFileId);
        });

        const configuredPrompt = String(existingConfig?.prompt || '').trim();
        if (configuredPrompt) promptFileNames.add(configuredPrompt);

        const desCsvReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, createServiceCsvFile, 'r');
        const desReadRes = await fetch(desCsvReadUrl);
        if (desReadRes.ok) {
          const desCsvText = await desReadRes.text();
          const updatedDesCsv = removeServiceRowFromCsv(desCsvText, serviceId, CREATE_SERVICE_CSV_FIELDS);
          if (updatedDesCsv !== desCsvText) {
            const desCsvWriteUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, createServiceCsvFile, 'rw');
            await putTextFile(desCsvWriteUrl, updatedDesCsv, 'text/csv; charset=utf-8');
          }
        } else if (desReadRes.status !== 404) {
          throw new Error(`Error al leer ${createServiceCsvFile} (${desReadRes.status}).`);
        }

        const deployCsvReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, deployServiceCsvFile, 'r');
        const deployReadRes = await fetch(deployCsvReadUrl);
        if (deployReadRes.ok) {
          const deployCsvText = await deployReadRes.text();
          const updatedDeployCsv = removeServiceRowFromCsv(deployCsvText, serviceId, CREATE_SERVICE_CSV_FIELDS);
          if (updatedDeployCsv !== deployCsvText) {
            const deployCsvWriteUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, deployServiceCsvFile, 'rw');
            await putTextFile(deployCsvWriteUrl, updatedDeployCsv, 'text/csv; charset=utf-8');
          }
        } else if (deployReadRes.status !== 404) {
          throw new Error(`Error al leer ${deployServiceCsvFile} (${deployReadRes.status}).`);
        }

        const configBlobName = `${serviceId}.json`;
        const configDeleteUrl = await getBlobUrlByConnectionString(connectionString, createServiceConfigContainer, configBlobName, 'd');
        await deleteBlobIfExists(configDeleteUrl);
      }

      if (processName) promptFileNames.add(`${processName}.txt`);

      await Promise.all([...promptFileNames].map(async (promptFileName) => {
        const devPromptBlob = `${promptsFolderName}/${promptFileName}`;
        const devPromptDeleteUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, devPromptBlob, 'd');
        await deleteBlobIfExists(devPromptDeleteUrl);

        const deployedPromptBlob = `${promptsDeployedFolderName}/${promptFileName}`;
        const deployedPromptDeleteUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, deployedPromptBlob, 'd');
        await deleteBlobIfExists(deployedPromptDeleteUrl);
      }));
    };

    await Promise.all(connectionStrings.map((connectionString) => deleteForConnectionString(connectionString)));

    await Promise.all([...documentBlobUrls].flatMap((blobUrl) =>
      connectionStrings.map((connectionString) => deleteStoredBlobUrlForConnectionString(connectionString, blobUrl))
    ));

    await Promise.all([...vectorStoreIds].map((vectorStoreId) => deleteVectorStore(vectorStoreId)));
    await Promise.all([...azureFileIds].map((fileId) => deleteAzureOpenAIFile(fileId)));
  };

  const cleanupIfCancelledGptCeu = async (row: CSVRow, nextState?: string) => {
    const state = nextState ?? getFieldValue(row, ['Estado_implementacion', 'Estado implementación']);
    if (!isCancelledState(state) || !isGptCeuFunctionality(row)) return;
    await cleanupGptCeuServiceFromAzure(row);
  };

  const syncCsvsOnStateChangeFromDetail = async (row: CSVRow, nextState: string) => {
    await cleanupIfCancelledGptCeu(row, nextState);

    const previousState = getServiceEstado(row);
    const processName = getServiceName(row);
    const idServicio = getFieldValue(row, ['ID']);
    const record = buildServiceCsvRecord(
      {
        ...row,
        Estado_implementacion: nextState,
        'Estado implementación': nextState,
      },
      {},
      currentUserEmail
    );

    // Implementado → En desarrollo: actualizar CSV des y quitar del CSV de producción.
    if (isImplementedState(previousState) && isDevelopmentState(nextState)) {
      if (!idServicio) {
        throw new Error('No se puede sincronizar el CSV: falta ID.');
      }
      requireBothBlobConnectionStrings();
      await Promise.all([
        upsertServiceRecordInCsvFile(primaryBlobConnectionString, createServiceCsvFile, record),
        upsertServiceRecordInCsvFile(secondaryBlobConnectionString, createServiceCsvFile, record),
      ]);
      await removeServiceFromProductionCsv(idServicio);
      return;
    }

    if (!isImplementedState(nextState)) return;

    if (!processName || !idServicio) {
      throw new Error('No se puede sincronizar el CSV: faltan NombreProceso o ID.');
    }

    await syncImplementedServiceCsvRecords(record);

    if (isImplementedState(previousState)) return;

    // Primera transición a Implementado: copiar prompt a carpeta desplegado en ambas cuentas.
    const deployPromptForConnectionString = async (connectionString: string) => {
      const configBlobName = `${idServicio}.json`;
      const configReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceConfigContainer, configBlobName, 'r');
      const configReadRes = await fetch(configReadUrl);
      if (!configReadRes.ok) {
        throw new Error(`Error al leer configuración ${configBlobName} (${configReadRes.status}).`);
      }
      const configJson = await configReadRes.json();
      const promptFileName = String(configJson?.prompt || '').trim() || `${processName}.txt`;

      const sourcePromptBlob = `${promptsFolderName}/${promptFileName}`;
      const sourcePromptReadUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, sourcePromptBlob, 'r');
      const promptContent = await readTextFile(sourcePromptReadUrl);

      const deployedPromptBlob = `${promptsDeployedFolderName}/${promptFileName}`;
      const deployedPromptWriteUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, deployedPromptBlob, 'rw');
      await putPromptFile(deployedPromptWriteUrl, promptContent);
    };

    requireBothBlobConnectionStrings();
    await Promise.all([
      deployPromptForConnectionString(primaryBlobConnectionString),
      deployPromptForConnectionString(secondaryBlobConnectionString),
    ]);
  };

  useEffect(() => {
    setRows(data);
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    const loadAreas = async () => {
      try {
        const nextAreas = await fetchServiceInitiativeAreas();
        if (!cancelled) setAreas(nextAreas);
      } catch (err) {
        console.error('Error cargando áreas de iniciativas:', err);
      }
    };
    loadAreas();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const loadDirectory = async () => {
      try {
        setDirectoryError(null);
        const url = await getDirectoryJsonUrl();
        const res = await fetch(url);
        if (!res.ok) throw new Error(`No se pudo leer el directorio (${res.status}).`);
        const json = await res.json();
        const collected: DirectoryEntry[] = [];
        collectDirectoryEntries(json, collected);
        const uniq = new Map<string, DirectoryEntry>();
        collected.forEach((e) => {
          if (!uniq.has(e.email)) uniq.set(e.email, e);
        });
        setDirectoryEntries([...uniq.values()].sort((a, b) => a.email.localeCompare(b.email)));
      } catch (err: any) {
        setDirectoryError(err.message || 'No se pudo cargar el directorio de usuarios.');
      }
    };
    loadDirectory();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDirectoryUsers = async () => {
      try {
        const users = await fetchDirectoryUsers();
        if (cancelled) return;
        setDirectoryUserEntries(mapDirectoryUsersToDirectoryEntries(users));
      } catch (err) {
        console.warn('No se pudieron cargar usuarios del directorio:', err);
      }
    };
    loadDirectoryUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  const centros = useMemo(() => {
    const unique = [
      ...new Set(
        rows
          .flatMap((row) => parseMultiOptionValues(row['Centro'], CENTRO_OPTIONS))
          .filter(Boolean)
      ),
    ];
    return ['TODOS', ...unique.sort()];
  }, [rows]);

  const areaOptions = useMemo(() => {
    const described = areas
      .map((area) => ({
        code: area.codigo.toUpperCase().trim(),
        label: area.descripcion.trim() || area.codigo,
      }))
      .filter((area) => area.code);
    const describedCodes = new Set(described.map((area) => area.code));
    const extraCodes = [
      ...new Set(
        rows
          .map((row) => (row.__codigo_area || '').toUpperCase().trim())
          .filter((code) => code && !describedCodes.has(code))
      ),
    ].sort();

    return [
      { code: 'TODAS', label: 'Todas las áreas' },
      ...described,
      ...extraCodes.map((code) => ({ code, label: code })),
      { code: 'SIN_AREA', label: 'Sin área asignada' },
    ];
  }, [areas, rows]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const estado = getServiceEstado(row);


      // Centro filter
      if (activeCategory !== 'TODOS' && !parseMultiOptionValues(row['Centro'], CENTRO_OPTIONS).includes(activeCategory)) return false;

      // Area filter
      const rowArea = (row.__codigo_area || '').toUpperCase().trim();
      if (selectedAreaCode === 'SIN_AREA') {
        if (rowArea) return false;
      } else if (selectedAreaCode !== 'TODAS' && rowArea !== selectedAreaCode) {
        return false;
      }

      // Estado filter
      if (filterEstado === 'VISIBLES') {
        if (!isDefaultVisible(row)) return false;
      } else if (filterEstado !== 'TODOS') {
        if (normalizeSearchText(estado) !== normalizeSearchText(filterEstado)) return false;
      }

      // Search filter
      if (search.trim() !== '') {
        const q = search.toLowerCase();
        const match = Object.values(row).some((v) => String(v).toLowerCase().includes(q));
        if (!match) return false;
      }

      return true;
    });
  }, [rows, activeCategory, selectedAreaCode, filterEstado, search]);

  // Default sorting by nombre de proceso/servicio
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const favoriteDiff = Number(isFavorite(b)) - Number(isFavorite(a));
      if (favoriteDiff !== 0) return favoriteDiff;

      const aDate = parseModifiedDate(getModifiedDateValue(a));
      const bDate = parseModifiedDate(getModifiedDateValue(b));
      const aTs = aDate ? aDate.getTime() : -Infinity;
      const bTs = bDate ? bDate.getTime() : -Infinity;
      if (aTs !== bTs) {
        return modifiedSortOrder === 'desc' ? bTs - aTs : aTs - bTs;
      }

      const aIsGpt = isGptCeuFunctionality(a);
      const bIsGpt = isGptCeuFunctionality(b);
      if (aIsGpt !== bIsGpt) return aIsGpt ? -1 : 1;

      const va = getServiceName(a);
      const vb = getServiceName(b);
      return va.localeCompare(vb, 'es', { numeric: true });
    });
  }, [filtered, modifiedSortOrder]);

  const totalPages = Math.ceil(sorted.length / rowsPerPage);
  const paginated = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const estadoCounts = useMemo(() => {
    const counts = { activo: 0, desarrollo: 0, pendiente: 0, otros: 0, favoritos: 0, total: rows.length };
    rows.forEach((row) => {
      const cls = estadoClass(getServiceEstado(row));
      if (cls === 'estado-activo') counts.activo += 1;
      else if (cls === 'estado-desarrollo') counts.desarrollo += 1;
      else if (cls === 'estado-pendiente') counts.pendiente += 1;
      else counts.otros += 1;
      if (isFavorite(row)) counts.favoritos += 1;
    });
    return counts;
  }, [rows]);

  const handleToggleView = (mode: 'grid' | 'table') => {
    setViewMode(mode);
    setPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDownload = () => {
    if (!rows.length) return;
    const csvContent = [
      columns.join(','),
      ...rows.map((row) =>
        columns.map((c) => `"${sanitizeCsvFieldValue(row[c]).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'datos.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildPendingTasksByInitiative = (tasks: ServiceTask[]) => {
    const map = new Map<string, ServiceTask[]>();
    tasks.forEach((task) => {
      if (task.estado === 'completed') return;
      const list = map.get(task.initiative_id) || [];
      list.push(task);
      map.set(task.initiative_id, list);
    });
    return map;
  };

  const handleExportInitiativesPdf = async (scope: 'all' | 'favorites' | 'filtered') => {
    setPdfMenuOpen(false);
    const sourceRows =
      scope === 'favorites'
        ? rows.filter((row) => isFavorite(row))
        : scope === 'filtered'
          ? sorted
          : rows.filter((row) => !String(row.__is_hidden || '').toLowerCase().includes('true'));

    if (!sourceRows.length) return;

    try {
      setGeneratingPdf(true);
      const [latestStatuses, allTasks] = await Promise.all([
        fetchLatestServiceStatusDescriptions(),
        fetchAllServiceTasks(),
      ]);
      exportServiceInitiativesPdfReport({
        title:
          scope === 'favorites'
            ? 'Estado Iniciativas GPT-CEU · Favoritas'
            : scope === 'filtered'
              ? 'Estado Iniciativas GPT-CEU · Vista filtrada'
              : 'Estado Iniciativas GPT-CEU',
        filenamePrefix:
          scope === 'favorites'
            ? 'informe_iniciativas_favoritas'
            : scope === 'filtered'
              ? 'informe_iniciativas_filtradas'
              : 'informe_iniciativas',
        rows: sourceRows,
        latestStatuses: new Map<string, ServiceStatusDescription>(
          latestStatuses.map((status) => [status.initiative_id, status])
        ),
        pendingTasksByInitiative: buildPendingTasksByInitiative(allTasks),
      });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'No se pudo generar el informe PDF.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const toggleFavorite = async (row: CSVRow) => {
    const rowKey = row.__database_id || getServiceName(row);
    if (!rowKey || savingFavoriteIds.has(rowKey)) return;
    const nextFavorite = !isFavorite(row);
    const previousRows = rows;
    const optimisticRow = { ...row, __is_favorite: String(nextFavorite) };

    setSavingFavoriteIds((prev) => new Set(prev).add(rowKey));
    setRows((prev) => prev.map((item) => (item === row || item.__database_id === row.__database_id ? optimisticRow : item)));
    setSelectedDetailRow((prev) => {
      if (!prev) return prev;
      return prev === row || prev.__database_id === row.__database_id ? optimisticRow : prev;
    });

    try {
      const savedRow = await setServiceInitiativeFavorite(row, nextFavorite);
      setRows((prev) => prev.map((item) => (item.__database_id === savedRow.__database_id ? savedRow : item)));
      setSelectedDetailRow((prev) => {
        if (!prev) return prev;
        return prev.__database_id === savedRow.__database_id ? savedRow : prev;
      });
    } catch (err) {
      setRows(previousRows);
      setModalError(err instanceof Error ? err.message : 'No se pudo actualizar el favorito.');
    } finally {
      setSavingFavoriteIds((prev) => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
    }
  };

  const resetCsvEnvEditorState = () => {
    setShowCsvEnvTabs(false);
    setEditorCsvTab('desarrollo');
    setDraftDes(null);
    setDraftProd(null);
    setUserFieldInputsDes({});
    setUserFieldInputsProd({});
    setRoleFieldInputsDes({});
    setRoleFieldInputsProd({});
    setCsvEnvLoading(false);
    setCsvEnvMissing({ desarrollo: false, produccion: false });
  };

  const openEditor = (row: CSVRow) => {
    const rowIdx = rows.findIndex((r) => r === row);
    if (rowIdx < 0) return;
    setSelectedRowIndex(rowIdx);
    setIsCreateMode(false);
    const nextRow = { ...row };
    if (!nextRow['servicio']) {
      nextRow['servicio'] = normalizeServicioValue(String(nextRow['LLM'] || '')) || 'Azure';
    } else {
      nextRow['servicio'] = normalizeServicioValue(String(nextRow['servicio'])) || String(nextRow['servicio']);
    }
    if (modifiedByColumn && currentUserEmail) {
      nextRow[modifiedByColumn] = currentUserEmail;
    }
    if (modifiedDateColumn) {
      nextRow[modifiedDateColumn] = new Date().toLocaleString('es-ES');
    }
    if (fechaColumn) {
      nextRow[fechaColumn] = new Date().toLocaleDateString('es-ES');
    }

    const nextUserInputs = buildUserFieldInputsFromRow(nextRow, userFieldSet, columns);
    const nextRoleInputs = buildRoleFieldInputsFromRow(nextRow, columns);
    const implemented = isImplementedState(getServiceEstado(nextRow));

    setDraftRow(nextRow);
    setUserFieldInputs(nextUserInputs);
    setRoleFieldInputs(nextRoleInputs);
    setActiveUserField(null);
    setModalError(null);
    setModalMessage(null);
    resetCsvEnvEditorState();

    if (!implemented) return;

    setShowCsvEnvTabs(true);
    setEditorCsvTab('desarrollo');
    setCsvEnvLoading(true);

    const serviceId = getFieldValue(nextRow, ['ID']);
    const processName = getServiceName(nextRow);
    void (async () => {
      try {
        if (!primaryBlobConnectionString || !secondaryBlobConnectionString) {
          throw new Error('Faltan REACT_APP_SM_BLOB_CONNECTION_STRING o REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING.');
        }
        if (!serviceId && !processName) {
          throw new Error('No se puede cargar el CSV: faltan ID o NombreProceso del servicio.');
        }

        // Desarrollo → blob primario. Producción → blob secundario (REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING).
        const [desRecord, prodRecord] = await Promise.all([
          readDevelopmentServiceRecord(serviceId, processName),
          readProductionServiceRecord(serviceId, processName),
        ]);

        const desDraft = applyServiceCsvRecordToDraft(nextRow, desRecord);
        const prodDraft = applyServiceCsvRecordToDraft(nextRow, prodRecord);
        const desUserInputs = buildUserFieldInputsFromRow(desDraft, userFieldSet, columns);
        const prodUserInputs = buildUserFieldInputsFromRow(prodDraft, userFieldSet, columns);
        const desRoleInputs = buildRoleFieldInputsFromRow(desDraft, columns);
        const prodRoleInputs = buildRoleFieldInputsFromRow(prodDraft, columns);

        setDraftDes(desDraft);
        setDraftProd(prodDraft);
        setUserFieldInputsDes(desUserInputs);
        setUserFieldInputsProd(prodUserInputs);
        setRoleFieldInputsDes(desRoleInputs);
        setRoleFieldInputsProd(prodRoleInputs);
        setCsvEnvMissing({ desarrollo: !desRecord, produccion: !prodRecord });

        setDraftRow(desDraft);
        setUserFieldInputs(desUserInputs);
        setRoleFieldInputs(desRoleInputs);
      } catch (err) {
        setModalError(err instanceof Error ? err.message : 'No se pudieron cargar los CSV de desarrollo/producción.');
        setShowCsvEnvTabs(false);
      } finally {
        setCsvEnvLoading(false);
      }
    })();
  };

  const openCreateEditor = () => {
    const emptyRow: CSVRow = formColumns.reduce((acc, col) => {
      acc[col] = '';
      return acc;
    }, {} as CSVRow);
    emptyRow['servicio'] = 'Azure';
    if (modifiedByColumn && currentUserEmail) {
      emptyRow[modifiedByColumn] = currentUserEmail;
    }
    if (modifiedDateColumn) {
      emptyRow[modifiedDateColumn] = new Date().toLocaleString('es-ES');
    }
    if (fechaColumn) {
      emptyRow[fechaColumn] = new Date().toLocaleDateString('es-ES');
    }

    setSelectedRowIndex(null);
    setIsCreateMode(true);
    setDraftRow(emptyRow);
    setUserFieldInputs({});
    setRoleFieldInputs({});
    setActiveUserField(null);
    setModalError(null);
    setModalMessage(null);
    resetCsvEnvEditorState();
  };

  const closeEditor = () => {
    setSelectedRowIndex(null);
    setIsCreateMode(false);
    setDraftRow(null);
    setUserFieldInputs({});
    setRoleFieldInputs({});
    setActiveUserField(null);
    setModalError(null);
    setModalMessage(null);
    resetCsvEnvEditorState();
  };

  const switchEditorCsvTab = (tab: EditorCsvTab) => {
    if (tab === editorCsvTab || !showCsvEnvTabs) return;

    if (editorCsvTab === 'desarrollo' && draftRow) {
      setDraftDes(draftRow);
      setUserFieldInputsDes(userFieldInputs);
      setRoleFieldInputsDes(roleFieldInputs);
    } else if (editorCsvTab === 'produccion' && draftRow) {
      setDraftProd(draftRow);
      setUserFieldInputsProd(userFieldInputs);
      setRoleFieldInputsProd(roleFieldInputs);
    }

    if (tab === 'desarrollo') {
      setDraftRow(draftDes || draftRow);
      setUserFieldInputs(userFieldInputsDes);
      setRoleFieldInputs(roleFieldInputsDes);
    } else {
      setDraftRow(draftProd || draftRow);
      setUserFieldInputs(userFieldInputsProd);
      setRoleFieldInputs(roleFieldInputsProd);
    }
    setEditorCsvTab(tab);
    setActiveUserField(null);
    setModalMessage(null);
  };

  const updateDraftField = (key: string, value: string) => {
    setDraftRow((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [key]: value };
      if (showCsvEnvTabs) {
        if (editorCsvTab === 'desarrollo') setDraftDes(next);
        else setDraftProd(next);
      }
      return next;
    });
  };

  const updateUserFieldInput = (key: string, value: string) => {
    setUserFieldInputs((prev) => {
      const next = { ...prev, [key]: value };
      if (showCsvEnvTabs) {
        if (editorCsvTab === 'desarrollo') setUserFieldInputsDes(next);
        else setUserFieldInputsProd(next);
      }
      return next;
    });
  };

  const setRoleFieldValue = (key: string, value: string[]) => {
    setRoleFieldInputs((prev) => {
      const next = { ...prev, [key]: value };
      if (showCsvEnvTabs) {
        if (editorCsvTab === 'desarrollo') setRoleFieldInputsDes(next);
        else setRoleFieldInputsProd(next);
      }
      return next;
    });
  };

  const closeMultiSelects = (except?: HTMLDetailsElement | null) => {
    document.querySelectorAll<HTMLDetailsElement>('.lm-multi-select[open]').forEach((details) => {
      if (details !== except) {
        details.open = false;
      }
    });
  };

  const toggleMultiOptionValue = (selected: string[], option: string): string[] => {
    return selected.includes(option)
      ? selected.filter((item) => item !== option)
      : [...selected, option];
  };

  const formatMultiOptionLabel = (selected: string[], placeholder: string): string => {
    return selected.length ? selected.join(', ') : placeholder;
  };

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest('.lm-multi-select')) {
        closeMultiSelects();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const applyUserSuggestion = (field: string, selectedEmail: string) => {
    const current = userFieldInputs[field] || '';
    const existingEmails = extractEmailsFromText(current);
    const normalizedSelected = normalizeEmail(selectedEmail);

    const withoutLastToken = current.replace(/[^,]*$/, '').trim();
    const baseEmails = withoutLastToken ? extractEmailsFromText(withoutLastToken) : existingEmails;
    const merged = [...baseEmails];

    if (!merged.includes(normalizedSelected)) {
      merged.push(normalizedSelected);
    }

    const formatted = merged.join(', ');
    updateUserFieldInput(field, `${formatted}${formatted ? ', ' : ''}`);
  };

  const handleSaveRow = async () => {
    if (!draftRow) return;
    try {
      setSavingRow(true);
      setModalError(null);
      setModalMessage(null);

      const normalizedDraft = { ...draftRow };
      if (modifiedByColumn && currentUserEmail) {
        normalizedDraft[modifiedByColumn] = currentUserEmail;
      }
      if (modifiedDateColumn) {
        normalizedDraft[modifiedDateColumn] = new Date().toLocaleString('es-ES');
      }
      if (fechaColumn) {
        normalizedDraft[fechaColumn] = new Date().toLocaleDateString('es-ES');
      }

      let nameByEmail = directoryNameByEmail;
      try {
        const freshDirectoryEntries = mapDirectoryUsersToDirectoryEntries(await fetchDirectoryUsers());
        setDirectoryUserEntries(freshDirectoryEntries);
        nameByEmail = buildNameByEmail(directoryEntries, freshDirectoryEntries);
      } catch (err) {
        console.warn('No se pudieron refrescar usuarios del directorio antes de guardar:', err);
      }

      const normalizedRow: CSVRow = columns.reduce((acc, col) => {
        if (userFieldSet.has(col)) {
          const typed = userFieldInputs[col] ?? '';
          const emails = extractEmailsFromText(typed);
          acc[col] = JSON.stringify(
            emails.map((email) => ({
              nombre: nameByEmail[email] || '',
              user_mail: email,
            }))
          );
        } else if (col.toLowerCase() === 'role') {
          acc[col] = JSON.stringify(roleFieldInputs[col] || []);
        } else if (col === 'Funcionalidad') {
          acc[col] = normalizeFuncionalidadValue(normalizedDraft[col]);
        } else {
          acc[col] = normalizedDraft[col] ?? '';
        }
        return acc;
      }, {} as CSVRow);
      const accessEmailsToEnsure = getAccessEmailsFromServicePeople(userFieldInputs, normalizedRow);
      const previousStateRaw =
        selectedRowIndex !== null ? getFieldValue(rows[selectedRowIndex], ['Estado_implementacion', 'Estado implementación']) : '';
      const currentStateRaw = getFieldValue(normalizedRow, ['Estado_implementacion', 'Estado implementación']);
      const isCancelledGptCeuSave = !isCreateMode && isCancelledState(currentStateRaw) && isGptCeuFunctionality(normalizedRow);
      const transitionedToImplemented = isImplementedState(currentStateRaw) && !isImplementedState(previousStateRaw);
      const transitionedFromImplementedToDevelopment =
        isImplementedState(previousStateRaw) && isDevelopmentState(currentStateRaw);

      if (isCancelledGptCeuSave) {
        await cleanupGptCeuServiceFromAzure(normalizedRow);
      } else if (isCreateMode) {
        const processName = getFieldValue(normalizedRow, ['NombreProceso', 'Nombre del servicio']);
        if (!processName) {
          throw new Error('El campo NombreProceso es obligatorio para crear un servicio.');
        }
        if (!primaryBlobConnectionString || !secondaryBlobConnectionString) {
          throw new Error('Faltan VITE_BLOB_CONNECTION_STRING o VITE_SECONDARY_BLOB_CONNECTION_STRING.');
        }

        const servicioValue = normalizeServicioValue(String(draftRow['servicio'] || ''));

        if (!servicioValue) {
          throw new Error('El campo servicio es obligatorio.');
        }
        const idServicio = getFieldValue(normalizedRow, ['ID']);
        if (!idServicio) {
          throw new Error('El campo ID es obligatorio para crear un servicio.');
        }

        const createRecord = buildServiceCsvRecord(normalizedRow, userFieldInputs, currentUserEmail);

        const csvHeader = CREATE_SERVICE_CSV_FIELDS.join(';');
        const csvRow = CREATE_SERVICE_CSV_FIELDS.map((field) => csvEscapeSemicolon(createRecord[field as keyof typeof createRecord] || '')).join(';');

        const writeForConnectionString = async (connectionString: string) => {
          const csvReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, createServiceCsvFile, 'r');
          let csvContent = '';
          const readRes = await fetch(csvReadUrl);
          if (readRes.ok) {
            csvContent = await readRes.text();
          } else if (readRes.status !== 404) {
            throw new Error(`Error leyendo CSV de alta (${readRes.status}).`);
          }

          const normalizedCsv = csvContent.trim() ? `${csvContent.trim()}\n${csvRow}` : `${csvHeader}\n${csvRow}`;
          const csvWriteUrl = await getBlobUrlByConnectionString(connectionString, createServiceCsvContainer, createServiceCsvFile, 'rw');
          await putTextFile(csvWriteUrl, normalizedCsv, 'text/csv; charset=utf-8');

          const promptBaseBlobName = `${promptsFolderName}/prompt_base.txt`;
          const promptBaseReadUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, promptBaseBlobName, 'r');
          const promptBaseContent = await readTextFile(promptBaseReadUrl);

          const promptFileName = `${processName}.txt`;
          const promptBlobName = `${promptsFolderName}/${promptFileName}`;
          const promptWriteUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, promptBlobName, 'rw');
          await putPromptFile(promptWriteUrl, promptBaseContent);

          const configBlobName = `${idServicio}.json`;
          const configWriteUrl = await getBlobUrlByConnectionString(connectionString, createServiceConfigContainer, configBlobName, 'rw');
          const configPayload = {
            id: idServicio,
            servicio: servicioValue,
            WhatsApp: getFieldValue(normalizedRow, ['Whatsapp']),
            prompt: promptFileName,
            Variables: {
              Fecha_de_hoy: 'datetime',
              Nombre_usuario: 'coger_nombre_usuario',
              Universidad_usuario: 'coger_universidad_usuario',
              Correo: 'user_mail',
              Perfil: 'coger_rol_usuario',
            },
          };
          await putTextFile(configWriteUrl, JSON.stringify(configPayload, null, 2), 'application/json; charset=utf-8');
        };

        await Promise.all([
          writeForConnectionString(primaryBlobConnectionString),
          writeForConnectionString(secondaryBlobConnectionString),
        ]);

        if (isImplementedState(currentStateRaw)) {
          await syncImplementedServiceCsvRecords(createRecord);
        }
      } else {
        const processName = getFieldValue(normalizedRow, ['NombreProceso', 'Nombre del servicio']);
        const idServicio = getFieldValue(normalizedRow, ['ID']);
        const servicioValue = normalizeServicioValue(String(draftRow['servicio'] || ''));
        const serviceCsvRecord = buildServiceCsvRecord(normalizedRow, userFieldInputs, currentUserEmail);

        if (!processName) {
          throw new Error('No se puede actualizar configuración sin NombreProceso.');
        }
        if (!idServicio) {
          throw new Error('No se puede actualizar configuración sin ID.');
        }
        if (!servicioValue) {
          throw new Error('El campo servicio es obligatorio para actualizar configuración.');
        }

        const writeServiceConfigJsonForConnectionString = async (connectionString: string) => {
          const configBlobName = `${idServicio}.json`;
          const configReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceConfigContainer, configBlobName, 'r');
          let existingConfig: Record<string, any> | null = null;
          try {
            const configReadRes = await fetch(configReadUrl);
            if (configReadRes.ok) {
              existingConfig = await configReadRes.json();
            }
          } catch {
            // Si no se puede leer la configuración existente, se usará el nombre del proceso
          }

          const existingPrompt = existingConfig?.prompt ? String(existingConfig.prompt).trim() : '';
          const promptValue = existingPrompt || `${processName}.txt`;

          const configWriteUrl = await getBlobUrlByConnectionString(connectionString, createServiceConfigContainer, configBlobName, 'rw');
          const configPayload = {
            ...existingConfig,
            id: idServicio,
            servicio: servicioValue,
            WhatsApp: getFieldValue(normalizedRow, ['Whatsapp']),
            prompt: promptValue,
            Variables: existingConfig?.Variables ?? {
              Fecha_de_hoy: 'datetime',
              Nombre_usuario: 'coger_nombre_usuario',
              Universidad_usuario: 'coger_universidad_usuario',
              Correo: 'user_mail',
              Perfil: 'coger_rol_usuario',
            },
          };
          await putTextFile(configWriteUrl, JSON.stringify(configPayload, null, 2), 'application/json; charset=utf-8');
        };

        const useCsvEnvTabSave =
          showCsvEnvTabs &&
          isImplementedState(currentStateRaw) &&
          isImplementedState(previousStateRaw);

        if (useCsvEnvTabSave) {
          // Pestaña activa: solo modifica su CSV (des → primario, prod → secundario).
          requireBothBlobConnectionStrings();
          if (editorCsvTab === 'desarrollo') {
            await upsertServiceRecordInCsvFile(primaryBlobConnectionString, createServiceCsvFile, serviceCsvRecord);
          } else {
            await upsertServiceRecordInCsvFile(secondaryBlobConnectionString, deployServiceCsvFile, serviceCsvRecord);
          }
        } else if (isImplementedState(currentStateRaw)) {
          // Transición a Implementado u otras rutas sin tabs: sincroniza des + prod.
          await syncImplementedServiceCsvRecords(serviceCsvRecord);
        } else {
          requireBothBlobConnectionStrings();
          await Promise.all([
            upsertServiceRecordInCsvFile(primaryBlobConnectionString, createServiceCsvFile, serviceCsvRecord),
            upsertServiceRecordInCsvFile(secondaryBlobConnectionString, createServiceCsvFile, serviceCsvRecord),
          ]);
          if (transitionedFromImplementedToDevelopment) {
            await removeServiceFromProductionCsv(idServicio);
          }
        }

        await Promise.all([
          writeServiceConfigJsonForConnectionString(primaryBlobConnectionString),
          writeServiceConfigJsonForConnectionString(secondaryBlobConnectionString),
        ]);
      }

      if (!isCancelledGptCeuSave && accessEmailsToEnsure.length > 0) {
        await Promise.all([
          ensureAccessCsvEmails(primaryBlobConnectionString, accessCsvContainerName, accessEmailsToEnsure),
          ensureAccessCsvEmails(secondaryBlobConnectionString, accessCsvContainerName, accessEmailsToEnsure),
        ]);
      }

      if (!isCancelledGptCeuSave && transitionedToImplemented) {
        const processName = getFieldValue(normalizedRow, ['NombreProceso', 'Nombre del servicio']);
        const idServicio = getFieldValue(normalizedRow, ['ID']);
        if (!processName) {
          throw new Error('No se puede desplegar: falta NombreProceso.');
        }
        if (!idServicio) {
          throw new Error('No se puede desplegar: falta ID.');
        }
        requireBothBlobConnectionStrings();

        // El registro CSV (des + prod) ya se sincronizó arriba al detectar estado Implementado.
        const deployPromptForConnectionString = async (connectionString: string) => {
          const configBlobName = `${idServicio}.json`;
          const configReadUrl = await getBlobUrlByConnectionString(connectionString, createServiceConfigContainer, configBlobName, 'r');
          const configReadRes = await fetch(configReadUrl);
          if (!configReadRes.ok) {
            throw new Error(`Error al leer configuración ${configBlobName} (${configReadRes.status}).`);
          }
          const configJson = await configReadRes.json();
          const promptFileName = String(configJson?.prompt || '').trim();
          if (!promptFileName) {
            throw new Error(`La configuración ${configBlobName} no contiene el campo "prompt".`);
          }

          const sourcePromptBlob = `${promptsFolderName}/${promptFileName}`;
          const sourcePromptReadUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, sourcePromptBlob, 'r');
          const promptContent = await readTextFile(sourcePromptReadUrl);

          const deployedPromptBlob = `${promptsDeployedFolderName}/${promptFileName}`;
          const deployedPromptWriteUrl = await getBlobUrlByConnectionString(connectionString, promptsContainerName, deployedPromptBlob, 'rw');
          await putPromptFile(deployedPromptWriteUrl, promptContent);
        };

        await Promise.all([
          deployPromptForConnectionString(primaryBlobConnectionString),
          deployPromptForConnectionString(secondaryBlobConnectionString),
        ]);
      }

      const savedRow = await upsertServiceInitiativeRow(normalizedRow);
      const updatedRows = [...rows];
      if (selectedRowIndex === null) {
        updatedRows.push(savedRow);
      } else {
        updatedRows[selectedRowIndex] = savedRow;
      }

      setRows(updatedRows);

      if (showCsvEnvTabs && isImplementedState(getServiceEstado(savedRow))) {
        const nextDraft = applyServiceCsvRecordToDraft(savedRow, buildServiceCsvRecord(normalizedRow, userFieldInputs, currentUserEmail));
        setDraftRow(nextDraft);
        if (editorCsvTab === 'desarrollo') {
          setDraftDes(nextDraft);
          setUserFieldInputsDes(userFieldInputs);
          setRoleFieldInputsDes(roleFieldInputs);
          setCsvEnvMissing((prev) => ({ ...prev, desarrollo: false }));
        } else {
          setDraftProd(nextDraft);
          setUserFieldInputsProd(userFieldInputs);
          setRoleFieldInputsProd(roleFieldInputs);
          setCsvEnvMissing((prev) => ({ ...prev, produccion: false }));
        }
        setModalMessage(
          editorCsvTab === 'desarrollo'
            ? 'Cambios guardados en el CSV de desarrollo.'
            : 'Cambios guardados en el CSV de producción.'
        );
      } else {
        setDraftRow({ ...savedRow });
        if (!isImplementedState(getServiceEstado(savedRow))) {
          resetCsvEnvEditorState();
        }
        setModalMessage('Cambios guardados correctamente.');
      }
      setIsCreateMode(false);
    } catch (err: any) {
      setModalError(err.message || 'No se pudo guardar la iniciativa.');
    } finally {
      setSavingRow(false);
    }
  };

  const handleDeleteRow = async () => {
    if (selectedRowIndex === null || selectedRowIndex < 0) return;
    const rowToDelete = rows[selectedRowIndex];
    const ok = window.confirm('¿Seguro que quieres eliminar este servicio? Se borrará completamente del sistema.');
    if (!ok) return;

    try {
      setDeletingRow(true);
      setModalError(null);
      setModalMessage(null);

      const updatedRows = rows.filter((_, idx) => idx !== selectedRowIndex);
      await deleteServiceInitiativeRow(rowToDelete);
      await cleanupGptCeuServiceFromAzure(rowToDelete);

      setRows(updatedRows);
      closeEditor();
    } catch (err: any) {
      setModalError(err.message || 'No se pudo eliminar el servicio.');
    } finally {
      setDeletingRow(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="error-container">
        <AlertCircle size={48} className="error-icon" />
        <h2>Error al cargar los datos</h2>
        <p>{error}</p>
        <button className="btn-primary" onClick={() => window.location.reload()}>
          <RefreshCw size={16} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className={`lp-with-chat${intakeChatCollapsed ? ' is-collapsed' : ''}`}>
    <div className="lp-dashboard">
      {/* Breadcrumb */}
      <nav className="lp-breadcrumb" aria-label="Ruta">
        <span className="lp-breadcrumb-item">Administración</span>
        <ChevronRight size={14} className="lp-breadcrumb-sep" />
        <span className="lp-breadcrumb-item lp-breadcrumb-current">Registro de servicios</span>
      </nav>

      {/* Header */}
      <header className="lp-header">
        <div className="lp-header-main">
          <div className="lp-header-eyebrow">
            <Sparkles size={12} />
            <span>Panel de control</span>
          </div>
          <h1 className="lp-header-title">Registro Maestro de Servicios</h1>
          <p className="lp-header-desc">
            Directorio unificado de servicios registrados en CEU. Gestiona, despliega y monitoriza desde un solo lugar.
          </p>
        </div>
        <div className="lp-header-actions">
          <div className="lp-pdf-menu">
            <button
              type="button"
              className="lp-btn lp-btn-secondary"
              onClick={() => setPdfMenuOpen((prev) => !prev)}
              disabled={generatingPdf || rows.length === 0}
              title="Generar informe PDF"
            >
              <FileDown size={15} />
              <span>{generatingPdf ? 'Generando...' : 'Informe PDF'}</span>
              <ChevronDown size={14} />
            </button>
            {pdfMenuOpen && (
              <>
                <button type="button" className="lp-pdf-backdrop" aria-label="Cerrar menú PDF" onClick={() => setPdfMenuOpen(false)} />
                <div className="lp-pdf-dropdown">
                  <div className="lp-pdf-dropdown-head">
                    <h3>Generar informe PDF</h3>
                    <p>Incluye estado actual y tareas pendientes de PostgreSQL</p>
                  </div>
                  <button type="button" className="lp-pdf-option" onClick={() => handleExportInitiativesPdf('all')}>
                    <span className="lp-pdf-option-icon">
                      <List size={16} />
                    </span>
                    <span>
                      <strong>Todas las iniciativas</strong>
                      <small>{rows.length} iniciativas</small>
                    </span>
                  </button>
                  <button type="button" className="lp-pdf-option" onClick={() => handleExportInitiativesPdf('favorites')} disabled={!rows.some((row) => isFavorite(row))}>
                    <span className="lp-pdf-option-icon is-favorite">
                      <Star size={16} fill="currentColor" />
                    </span>
                    <span>
                      <strong>Solo favoritas</strong>
                      <small>{rows.filter((row) => isFavorite(row)).length} iniciativas favoritas</small>
                    </span>
                  </button>
                  <button type="button" className="lp-pdf-option" onClick={() => handleExportInitiativesPdf('filtered')} disabled={!sorted.length}>
                    <span className="lp-pdf-option-icon is-filtered">
                      <Filter size={16} />
                    </span>
                    <span>
                      <strong>Vista filtrada</strong>
                      <small>{sorted.length} iniciativas visibles ahora</small>
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
          <button className="lp-btn lp-btn-secondary" onClick={handleDownload} title="Descargar CSV">
            <Download size={15} />
            <span>Exportar</span>
          </button>
          <button className="lp-btn lp-btn-primary" onClick={openCreateEditor} title="Crear servicio nuevo">
            <PlusCircle size={15} />
            <span>Nuevo servicio</span>
          </button>
        </div>
      </header>

      {/* Stat summary strip (compact, minimal) */}
      <div className="lp-stat-strip">
        <div className="lp-stat-item">
          <span className="lp-stat-label">Total</span>
          <span className="lp-stat-value">{estadoCounts.total}</span>
        </div>
        <div className="lp-stat-divider" />
        <div className="lp-stat-item">
          <span className="lp-stat-dot lp-dot-activo" />
          <span className="lp-stat-label">Activos</span>
          <span className="lp-stat-value">{estadoCounts.activo}</span>
        </div>
        <div className="lp-stat-item">
          <span className="lp-stat-dot lp-dot-desarrollo" />
          <span className="lp-stat-label">Desarrollo</span>
          <span className="lp-stat-value">{estadoCounts.desarrollo}</span>
        </div>
        <div className="lp-stat-item">
          <span className="lp-stat-dot lp-dot-pendiente" />
          <span className="lp-stat-label">Pendientes</span>
          <span className="lp-stat-value">{estadoCounts.pendiente}</span>
        </div>
        <div className="lp-stat-item">
          <Star size={14} className="lp-stat-star" />
          <span className="lp-stat-label">Favoritas</span>
          <span className="lp-stat-value">{estadoCounts.favoritos}</span>
        </div>
        <div className="lp-stat-divider" />
        <div className="lp-stat-item lp-stat-filtered">
          <span className="lp-stat-label">Mostrando</span>
          <span className="lp-stat-value lp-stat-value-accent">{sorted.length}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="lp-toolbar">
        <div className="lp-search">
          <Search size={15} className="lp-search-icon" />
          <input
            type="text"
            placeholder="Buscar servicio, responsable, ID..."
            value={search}
            onChange={handleSearch}
            className="lp-search-input"
          />
          {search && (
            <button className="lp-search-clear" onClick={() => { setSearch(''); setPage(1); }} aria-label="Limpiar búsqueda">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="lp-toolbar-filters">
          <div className="lp-select">
            <Filter size={13} className="lp-select-icon" />
            <select
              value={activeCategory}
              onChange={(e) => { setActiveCategory(e.target.value); setPage(1); }}
              aria-label="Filtrar por centro"
            >
              {centros.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === 'TODOS' ? 'Todos los centros' : cat}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <Building2 size={13} className="lp-select-icon" />
            <select
              value={selectedAreaCode}
              onChange={(e) => { setSelectedAreaCode(e.target.value); setPage(1); }}
              aria-label="Filtrar por área"
            >
              {areaOptions.map((area) => (
                <option key={area.code} value={area.code}>
                  {area.label}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <Activity size={13} className="lp-select-icon" />
            <select
              value={filterEstado}
              onChange={(e) => { setFilterEstado(e.target.value); setPage(1); }}
              aria-label="Filtrar por estado"
            >
              <option value="VISIBLES">Implementadas, desarrollo y favoritas</option>
              <option value="TODOS">Todos los estados</option>
              {ESTADO_OPTIONS.map((estado) => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <ArrowUpDown size={13} className="lp-select-icon" />
            <select
              value={modifiedSortOrder}
              onChange={(e) => { setModifiedSortOrder(e.target.value as 'desc' | 'asc'); setPage(1); }}
              aria-label="Ordenar por fecha"
            >
              <option value="desc">Más recientes</option>
              <option value="asc">Más antiguos</option>
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>
        </div>

        <div className="lp-view-toggle" role="group" aria-label="Cambiar vista">
          <button
            type="button"
            className={`lp-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => handleToggleView('grid')}
            aria-pressed={viewMode === 'grid'}
            title="Vista de cuadrícula"
          >
            <LayoutGrid size={15} />
          </button>
          <button
            type="button"
            className={`lp-view-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => handleToggleView('table')}
            aria-pressed={viewMode === 'table'}
            title="Vista de tabla"
          >
            <Rows3 size={15} />
          </button>
        </div>
      </div>

      {/* Results */}
      {paginated.length === 0 ? (
        <div className="lp-empty">
          <div className="lp-empty-icon">
            <Search size={28} />
          </div>
          <h3>No se encontraron resultados</h3>
          <p>
            {search
              ? <>No hay servicios que coincidan con <strong>"{search}"</strong></>
              : 'Ajusta los filtros o crea un nuevo servicio para empezar.'}
          </p>
          {search && (
            <button className="lp-btn lp-btn-ghost" onClick={() => { setSearch(''); setPage(1); }}>
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="lp-grid">
          {paginated.map((row, i) => {
            const estado = getServiceEstado(row);
            const estadoCls = estadoClass(estado);
            const modifiedRaw = getModifiedDateValue(row);
            const modifiedDate = parseModifiedDate(modifiedRaw);
            const favorite = isFavorite(row);
            const responsables = parseUsersList(row['Responsable servicio']);
            const tecnicos = parseUsersList(row['Técnico']);
            const roles = parseRoleLabels(row['Role'] || row['ROL']);
            const favoriteKey = row.__database_id || getServiceName(row);
            const favoriteSaving = favoriteKey ? savingFavoriteIds.has(favoriteKey) : false;
            return (
              <article
                key={i}
                className={`lp-card ${favorite ? 'is-favorite' : ''}`}
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <div className="lp-card-top">
                  <div className="lp-card-badges">
                    {row['Centro'] && (
                      <span className="lp-chip lp-chip-center">
                        <Building2 size={12} />
                        {row['Centro']}
                      </span>
                    )}
                    <span className={`lp-badge lp-badge-estado ${estadoCls}`}>
                      <span className="lp-badge-dot" />
                      {estado || 'Sin estado'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`lp-favorite-mark ${favorite ? 'is-active' : ''}`}
                    title={favorite ? 'Quitar de favoritas' : 'Marcar como favorita'}
                    aria-label={favorite ? 'Quitar de favoritas' : 'Marcar como favorita'}
                    aria-pressed={favorite}
                    disabled={favoriteSaving}
                    onClick={() => toggleFavorite(row)}
                  >
                    <Star size={18} fill={favorite ? 'currentColor' : 'none'} />
                  </button>
                </div>
                <h3 className="lp-card-title">
                  {getServiceName(row) || 'Servicio sin nombre'}
                </h3>
                {row['descripcion'] && (
                  <p className="lp-card-desc">{row['descripcion']}</p>
                )}
                {roles.length > 0 && (
                  <div className="lp-card-tags" aria-label="Roles">
                    {roles.slice(0, 4).map((role) => (
                      <span key={role} className="lp-role-chip">{role}</span>
                    ))}
                  </div>
                )}
                <div className="lp-card-meta">
                  {responsables.length > 0 && (
                    <div className="lp-card-meta-block">
                      <div className="lp-card-meta-heading">
                        <Users size={14} />
                        <span>Responsables</span>
                      </div>
                      <div className="lp-person-list">
                        {responsables.slice(0, 3).map((responsable) => (
                          <span key={responsable} className="lp-person-chip">{responsable}</span>
                        ))}
                        {responsables.length > 3 && <span className="lp-person-more">+{responsables.length - 3}</span>}
                      </div>
                    </div>
                  )}
                  {tecnicos.length > 0 && (
                    <div className="lp-card-meta-block">
                      <div className="lp-card-meta-heading">
                        <User size={14} />
                        <span>Técnicos</span>
                      </div>
                      <div className="lp-person-list">
                        {tecnicos.slice(0, 3).map((tecnico) => (
                          <span key={tecnico} className="lp-person-chip lp-person-chip-tech">{tecnico}</span>
                        ))}
                        {tecnicos.length > 3 && <span className="lp-person-more">+{tecnicos.length - 3}</span>}
                      </div>
                    </div>
                  )}
                  {modifiedDate && (
                    <div className="lp-card-meta-row">
                      <Calendar size={13} className="lp-card-meta-icon" />
                      <span className="lp-card-meta-text">
                        {modifiedDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                </div>
                <div className="lp-card-footer">
                  <button type="button" className="lp-card-open" onClick={() => openEditor(row)}>
                    Abrir
                  </button>
                  <button type="button" className="lp-card-details" onClick={() => setSelectedDetailRow(row)}>
                    Ver detalles
                    <ChevronRight size={14} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="lp-table-wrap">
          <table className="lp-table">
            <thead>
              <tr>
                <th className="lp-th-indicator" aria-label="Estado" />
                <th>Servicio</th>
                <th>Centro</th>
                <th>Estado</th>
                <th>Responsable</th>
                <th className="lp-th-date">Modificado</th>
                <th className="lp-th-actions" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {paginated.map((row, i) => {
                const estado = getServiceEstado(row);
                const estadoCls = estadoClass(estado);
                const modifiedRaw = getModifiedDateValue(row);
                const modifiedDate = parseModifiedDate(modifiedRaw);
                const favorite = isFavorite(row);
                const favoriteKey = row.__database_id || getServiceName(row);
                const favoriteSaving = favoriteKey ? savingFavoriteIds.has(favoriteKey) : false;
                return (
                  <tr
                    key={i}
                    className={`lp-tr ${favorite ? 'is-favorite' : ''}`}
                    style={{ animationDelay: `${i * 18}ms` }}
                  >
                    <td className="lp-td-indicator">
                      <span className={`lp-td-dot ${estadoCls}`} />
                    </td>
                    <td className="lp-td-name">
                      <div className="lp-td-name-main">
                        <button
                          type="button"
                          className={`lp-td-star ${favorite ? 'is-active' : ''}`}
                          title={favorite ? 'Quitar de favoritas' : 'Marcar como favorita'}
                          aria-label={favorite ? 'Quitar de favoritas' : 'Marcar como favorita'}
                          aria-pressed={favorite}
                          disabled={favoriteSaving}
                          onClick={() => toggleFavorite(row)}
                        >
                          <Star size={14} fill={favorite ? 'currentColor' : 'none'} />
                        </button>
                        {getServiceName(row) || 'Servicio sin nombre'}
                      </div>
                      {row['ID'] && <div className="lp-td-name-sub">ID: {row['ID']}</div>}
                    </td>
                    <td>{row['Centro'] ? <span className="lp-chip lp-chip-sm">{row['Centro']}</span> : <span className="lp-muted">—</span>}</td>
                    <td>
                      <span className={`lp-badge lp-badge-estado ${estadoCls}`}>
                        <span className="lp-badge-dot" />
                        {estado || 'Sin estado'}
                      </span>
                    </td>
                    <td className="lp-td-responsable">
                      <span title={parseUsers(row['Responsable servicio'])}>
                        {parseUsers(row['Responsable servicio'])}
                      </span>
                    </td>
                    <td className="lp-td-date">
                      {modifiedDate
                        ? modifiedDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                        : <span className="lp-muted">—</span>}
                    </td>
                    <td className="lp-td-actions">
                      <button type="button" className="lp-table-action" onClick={() => openEditor(row)}>
                        Abrir
                      </button>
                      <button type="button" className="lp-table-action lp-table-action-primary" onClick={() => setSelectedDetailRow(row)}>
                        Ver detalles
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="lp-pagination">
          <button
            className="lp-page-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Anterior
          </button>
          <div className="lp-page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 ||
                  p === totalPages ||
                  Math.abs(p - page) <= 2
              )
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) {
                  acc.push('...');
                }
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '...' ? (
                  <span key={`dots-${i}`} className="lp-page-dots">…</span>
                ) : (
                  <button
                    key={p}
                    className={`lp-page-num ${page === p ? 'active' : ''}`}
                    onClick={() => setPage(p as number)}
                  >
                    {p}
                  </button>
                )
              )}
          </div>
          <button
            className="lp-page-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Siguiente →
          </button>
        </div>
      )}

      {selectedDetailRow && (
        <ServiceInitiativeDetailModal
          row={selectedDetailRow}
          areas={areas}
          onRowUpdated={(updatedRow) => {
            setRows((prev) => prev.map((item) => (item.__database_id === updatedRow.__database_id ? updatedRow : item)));
            setSelectedDetailRow(updatedRow);
          }}
          onBeforeStateChange={syncCsvsOnStateChangeFromDetail}
          onClose={() => setSelectedDetailRow(null)}
        />
      )}

      {/* Pop-up Modal */}
      {draftRow && (
        <div className="list-modal-overlay">
          <div className="list-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="list-modal-close" onClick={closeEditor}>
              <X size={24} />
            </button>
            <div className="list-modal-header-section">
              <span className={`svc-estado-badge ${estadoClass(getServiceEstado(draftRow))}`}>
                {getServiceEstado(draftRow) || 'Sin Estado'}
              </span>
              <h2>{isCreateMode ? 'Nuevo servicio' : getServiceName(draftRow) || 'Servicio sin nombre'}</h2>
            </div>

            <div className="list-modal-body">
              <div className="list-modal-section">
                <h3>Editar Registro de Iniciativa GPT-CEU</h3>
                {directoryError && (
                  <div className="error-alert">
                    <AlertCircle size={16} />
                    {directoryError}
                  </div>
                )}
                {modalError && (
                  <div className="error-alert">
                    <AlertCircle size={16} />
                    {modalError}
                  </div>
                )}
                {modalMessage && <div className="success-alert">{modalMessage}</div>}

                {showCsvEnvTabs && (
                  <div className="list-modal-csv-tabs" role="tablist" aria-label="Entorno CSV">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={editorCsvTab === 'desarrollo'}
                      className={`list-modal-csv-tab ${editorCsvTab === 'desarrollo' ? 'active' : ''}`}
                      onClick={() => switchEditorCsvTab('desarrollo')}
                      disabled={csvEnvLoading || savingRow || deletingRow}
                    >
                      Desarrollo
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={editorCsvTab === 'produccion'}
                      className={`list-modal-csv-tab ${editorCsvTab === 'produccion' ? 'active' : ''}`}
                      onClick={() => switchEditorCsvTab('produccion')}
                      disabled={csvEnvLoading || savingRow || deletingRow}
                    >
                      Producción
                    </button>
                  </div>
                )}
                {showCsvEnvTabs && csvEnvLoading && (
                  <p className="list-modal-csv-hint">Cargando registros CSV de desarrollo y producción...</p>
                )}
                {showCsvEnvTabs && !csvEnvLoading && csvEnvMissing[editorCsvTab] && (
                  <div className="error-alert">
                    <AlertCircle size={16} />
                    {editorCsvTab === 'desarrollo'
                      ? `No se encontró este servicio en ${createServiceCsvFile} (blob desarrollo). Puedes completar los datos y guardar para crearlo en desarrollo.`
                      : `No se encontró este servicio en ${deployServiceCsvFile} (blob producción / REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING). Puedes completar los datos y guardar para crearlo en producción.`}
                  </div>
                )}
                {showCsvEnvTabs && !csvEnvLoading && (
                  <p className="list-modal-csv-hint">
                    {editorCsvTab === 'desarrollo'
                      ? 'Estás editando el CSV de desarrollo. Guardar no modificará producción.'
                      : 'Estás editando el CSV de producción. Guardar no modificará desarrollo.'}
                  </p>
                )}

                <div className={`list-modal-edit-grid ${csvEnvLoading ? 'is-loading' : ''}`}>
                  {formColumns.filter((col) => {
                    const normalized = normalizeSearchText(col);
                    if (normalized === 'ruta carpeta') return false;
                    if (normalized === 'modificado por') return false;
                    if (normalized === 'modificado') return false;
                    if (normalized === 'fecha') return false;
                    if (normalized === 'sinidentificacion') return false;

                    return true;
                  }).map((col) => {
                    const isUserField = userFieldSet.has(col);
                    const value = isUserField ? (userFieldInputs[col] ?? '') : (draftRow?.[col] ?? '');
                    const useTextarea = value.length > 90 || value.includes('\n');
                    const isRoleField = col.toLowerCase() === 'role';
                    const isFuncionalidadField = col === 'Funcionalidad';
                    const isCentroField = col === 'Centro';
                    const isEstadoField = normalizeSearchText(col).replace(/_/g, ' ') === 'estado implementacion';
                    const isWhatsappField = col.toLowerCase() === 'whatsapp';
                    const isSinIdentField = col.toLowerCase() === 'sinidentificacion';
                    const isLlmField = col.toLowerCase() === 'llm';
                    const isServicioField = col.toLowerCase() === 'servicio';
                    const isDateField = col === 'Fecha' || col === 'Fecha_novedad_proceso';
                    const typedToken = (value.split(',').pop() || '').trim();
                    const suggestions =
                      isUserField && typedToken
                        ? userLookupEntries
                          .map((entry) => ({ entry, score: scoreDirectoryEntry(entry, typedToken) }))
                          .filter((x) => x.score >= 0)
                          .sort((a, b) => {
                            if (b.score !== a.score) return b.score - a.score;
                            return a.entry.email.localeCompare(b.entry.email, 'es');
                          })
                          .map((x) => x.entry)
                          .slice(0, 8)
                        : [];
                    return (
                      <div key={col} className="list-modal-item full-width">
                        <label className="lm-label" htmlFor={`field-${col}`}>{col.replaceAll('_', ' ')}</label>
                        {isUserField ? (
                          <>
                            <input
                              id={`field-${col}`}
                              className="lm-input"
                              value={value}
                              onFocus={() => setActiveUserField(col)}
                              onBlur={() => setTimeout(() => setActiveUserField((prev) => (prev === col ? null : prev)), 120)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && suggestions.length > 0) {
                                  e.preventDefault();
                                  applyUserSuggestion(col, suggestions[0].email);
                                }
                              }}
                              onChange={(e) => updateUserFieldInput(col, e.target.value)}
                              placeholder="correo1@dominio.com, correo2@dominio.com"
                            />
                            {activeUserField === col && suggestions.length > 0 && (
                              <div className="user-suggest-box">
                                {suggestions.map((entry) => (
                                  <button
                                    type="button"
                                    key={`${col}-${entry.email}`}
                                    className="user-suggest-item"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applyUserSuggestion(col, entry.email)}
                                  >
                                    <span>{entry.email}</span>
                                    {entry.name && <small>{entry.name}</small>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        ) : isRoleField ? (
                          <details id={`field-${col}`} className="lm-multi-select" onToggle={(e) => e.currentTarget.open && closeMultiSelects(e.currentTarget)}>
                            <summary className="lm-input lm-multi-summary">
                              {formatMultiOptionLabel(roleFieldInputs[col] || [], 'Selecciona roles')}
                            </summary>
                            <div className="lm-multi-options">
                              {ROLE_OPTIONS.map((opt) => {
                                const selectedRoles = roleFieldInputs[col] || [];
                                return (
                                  <label key={opt} className="lm-multi-option">
                                    <input
                                      type="checkbox"
                                      checked={selectedRoles.includes(opt)}
                                      onChange={() => setRoleFieldValue(col, toggleMultiOptionValue(selectedRoles, opt))}
                                    />
                                    <span>{opt}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </details>
                        ) : isFuncionalidadField ? (
                          <select
                            id={`field-${col}`}
                            className="lm-input"
                            value={value}
                            onChange={(e) => updateDraftField(col, e.target.value)}
                          >
                            <option value="">Selecciona funcionalidad</option>
                            {FUNCIONALIDAD_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : isCentroField ? (
                          <details id={`field-${col}`} className="lm-multi-select" onToggle={(e) => e.currentTarget.open && closeMultiSelects(e.currentTarget)}>
                            <summary className="lm-input lm-multi-summary">
                              {formatMultiOptionLabel(parseMultiOptionValues(value, CENTRO_OPTIONS), 'Selecciona centros')}
                            </summary>
                            <div className="lm-multi-options">
                              {CENTRO_OPTIONS.map((opt) => {
                                const selectedCentros = parseMultiOptionValues(value, CENTRO_OPTIONS);
                                return (
                                  <label key={opt} className="lm-multi-option">
                                    <input
                                      type="checkbox"
                                      checked={selectedCentros.includes(opt)}
                                      onChange={() => updateDraftField(
                                        col,
                                        toggleMultiOptionValue(selectedCentros, opt).join(',')
                                      )}
                                    />
                                    <span>{opt}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </details>
                        ) : isEstadoField ? (
                          <select
                            id={`field-${col}`}
                            className="lm-input"
                            value={value}
                            onChange={(e) => updateDraftField(col, e.target.value)}
                          >
                            <option value="">Selecciona estado</option>
                            {ESTADO_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : isWhatsappField || isSinIdentField ? (
                          <select
                            id={`field-${col}`}
                            className="lm-input"
                            value={String(value).replace('_', ' ')}
                            onChange={(e) => updateDraftField(col, e.target.value)}
                          >
                            <option value="">Selecciona valor</option>
                            {BOOL_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : isLlmField ? (
                          <div className="tag-select-wrap">
                            {['Openai', 'Azure'].map((opt) => {
                              const active = String(value).toLowerCase() === opt.toLowerCase();
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  className={`tag-pill ${active ? 'active' : ''}`}
                                  onClick={() => updateDraftField(col, opt)}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        ) : isServicioField ? (
                          <select
                            id={`field-${col}`}
                            className="lm-input"
                            value={normalizeServicioValue(String(value)) || String(value)}
                            onChange={(e) => updateDraftField(col, e.target.value)}
                          >
                            <option value="">Selecciona servicio</option>
                            {SERVICIO_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : isDateField ? (
                          <input
                            id={`field-${col}`}
                            type="date"
                            className="lm-input"
                            value={toISODateInput(value)}
                            onChange={(e) => updateDraftField(col, e.target.value)}
                          />
                        ) : useTextarea ? (
                          <textarea
                            id={`field-${col}`}
                            className="lm-input lm-textarea"
                            value={value}
                            onChange={(e) => updateDraftField(col, e.target.value)}
                          />
                        ) : (
                          <input
                            id={`field-${col}`}
                            className="lm-input"
                            value={value}
                            onChange={(e) => updateDraftField(col, e.target.value)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="list-modal-meta">
                  {!isCreateMode && (
                    <button className="btn-danger" onClick={handleDeleteRow} disabled={savingRow || deletingRow}>
                      <Trash2 size={16} />
                      {deletingRow ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  )}
                  <button className="btn-outline" onClick={closeEditor} disabled={savingRow || deletingRow}>
                    Cancelar
                  </button>
                  <button className="btn-primary" onClick={handleSaveRow} disabled={savingRow || deletingRow || csvEnvLoading}>
                    {savingRow ? <RefreshCw size={16} className="spinner-icon" /> : <Save size={16} />}
                    {savingRow
                      ? 'Guardando...'
                      : showCsvEnvTabs
                        ? (editorCsvTab === 'desarrollo' ? 'Guardar desarrollo' : 'Guardar producción')
                        : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    <IntakeChatPanel
      collapsed={intakeChatCollapsed}
      onToggle={() => setIntakeChatCollapsed((prev) => !prev)}
    />
    </div>
  );
};

export default ListPage;
